/* eslint-disable */
import { API_URL } from '../../config';
import React, { useState, useMemo, useEffect } from 'react';
import { useAppData } from '../../context/AppDataContext';
import { propertiesAPI, paymentsAPI, expensesAPI, calendarAPI, tenantsAPI, serviceRequestsAPI, documentsAPI } from '../../services/api';
import ContractorPicker from '../contractors/ContractorPicker';
import { formatCurrency, formatDate, getCurrentMonth, formatMonth } from '../../utils/helpers';
import { effectiveJurisdiction } from '../../utils/propertyCountry';
import { symbolFor } from '../../utils/currencyTotals';
import { PROPERTY_EXPENSE_CATEGORIES, categoryOptions, categoryHint } from '../../utils/expenseCategories';

const EXPENSE_CATEGORIES = PROPERTY_EXPENSE_CATEGORIES;
const LICENCE_TYPES = ['Building Regulations','Contracts','EICR (Electrical)','EPC (Energy Performance)','Electrical Installation Certificate','Fire Safety Certificate','Gas Safety Certificate','HMO Licence','Insurance Certificate','Legionella Risk Assessment','Other','PAT Testing','Planning Permission'];

// Document types: compliance types have expiry dates + calendar reminders; general types don't
const COMPLIANCE_TYPES = ['Building Regulations','EICR (Electrical)','EPC (Energy Performance)','Electrical Installation Certificate','Fire Safety Certificate','Gas Safety Certificate','HMO Licence','Insurance Certificate','Legionella Risk Assessment','PAT Testing','Planning Permission'];
const GENERAL_TYPES    = ['Photos','Contracts','Floor Plan','Inventory','Other Document'];
const ALL_DOC_TYPES    = [...COMPLIANCE_TYPES.slice().sort(), ...GENERAL_TYPES];
const isComplianceType = (t) => COMPLIANCE_TYPES.includes(t);

const ACCEPTED_FILES = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.ppt,.pptx';
const UPLOAD_HELP = 'Max 10MB · PDF, JPEG, PNG, Word, Excel, PowerPoint';
const isImageFile = (mime, name) => (mime||'').startsWith('image/') || /\.(jpe?g|png)$/i.test(name||'');

const defaultLicenceForm = { type:'', description:'', referenceNumber:'', validFrom:'', validTo:'', notes:'' };

export default function PropertyDetail({ property, onBack, onDelete, onUpdate, onEdit, initialTab, initialAddExpense }) {
  const { payments, setPayments, expenses, setExpenses, calendarTasks, setCalendarTasks } = useAppData();
  // BUG FIX: always defaulted to 'rooms', including for Single Let/BTL
  // properties that have no rooms tab at all (see below) — opened on a
  // tab that either wouldn't exist or would show nothing useful. Defaults
  // to 'tenants' for anything that isn't HMO, since that's what actually
  // matters for a property with no room structure.
  const [activeTab, setActiveTab] = useState(initialTab || ((property.property_type || property.propertyType || 'hmo') === 'hmo' ? 'rooms' : 'tenants'));
  // BUG FIX: loadPropTenants was only ever triggered by explicitly clicking
  // the Tenants tab button — fine when a user has to click their way there,
  // but now that non-HMO properties default straight onto this tab (see
  // above), the component can mount already showing it with no click ever
  // having happened. The click handler never ran, so propTenants stayed
  // permanently empty — a newly added tenant existed correctly in the
  // Tenants module but the property page had no way of knowing to fetch it.
  // Runs once on mount, checking whatever tab is active at that moment;
  // the click handler already correctly covers any later tab switch.
  // BUG FIX: broadened while sweeping for further fallout from today's BTL
  // changes — the always-visible header stats (Monthly Rent, Gross Yield)
  // now source from propTenants for non-HMO properties too, not just the
  // Tenants tab content. Loading it only when the initial tab happened to
  // be 'tenants' left those stats showing £0 whenever a non-default tab
  // was opened first. isHMO (the top-level constant) isn't in scope yet at
  // this point in the file, so checked inline against the property prop,
  // same as elsewhere in this file for the same ordering reason.
  useEffect(() => {
    const propertyIsHMO = (property.property_type || property.propertyType || 'hmo') === 'hmo';
    if (activeTab === 'tenants' || !propertyIsHMO) loadPropTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentWarning, setPaymentWarning] = useState(null);

  // Payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ roomId:'', month:getCurrentMonth(), amount:'', paidDate:new Date().toISOString().split('T')[0], tenantId:'' });
  const [editingPayment, setEditingPayment] = useState(null);
  const [addExpenseAfterPayment, setAddExpenseAfterPayment] = useState(false);
  const [quickExpenses, setQuickExpenses] = useState([{ category:'', amount:'', description:'', paid:true }]);
  const addQuickExpenseRow = () => setQuickExpenses(p => [...p, { category:'', amount:'', description:'', paid:true }]);
  const removeQuickExpenseRow = (i) => setQuickExpenses(p => p.filter((_,j) => j !== i));
  const setQuickExpenseField = (i, field, value) => setQuickExpenses(p => p.map((row,j) => j===i ? {...row,[field]:value} : row));

  // Expense modal
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ category:'', amount:'', date:new Date().toISOString().split('T')[0], description:'', paid:false });

  // DEEPLINK-001: Arrived here via a report's "Add Expense" link — jump
  // straight to the Expenses tab with the form open and category pre-filled.
  useEffect(() => {
    if (initialAddExpense) {
      setActiveTab('expenses');
      setExpenseForm(p => ({ ...p, category: initialAddExpense.category || p.category }));
      setShowExpenseModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Room modals
  const [editingRoom, setEditingRoom] = useState(null);
  const [showVacantModal, setShowVacantModal] = useState(false);
  const [vacantRoom, setVacantRoom] = useState(null);
  const [vacantForm, setVacantForm] = useState({ date:new Date().toISOString().split('T')[0], notes:'' });
  const [showAddRoomModal, setShowAddRoomModal] = useState(false);
  const [newRoomForm, setNewRoomForm] = useState({ roomName:'', tenantName:'', rentAmount:'', paymentDueDay:1, status:'Vacant', leaseStart:'', leaseEnd:'' });

  // Tenant management state
  const [propTenants, setPropTenants] = useState([]);

  // BACKDATE-002: Who actually occupied this room during the selected
  // month — including past tenants — rather than only ever offering
  // whoever lives there today. This is what fixes a backdated payment
  // showing against the wrong (current) tenant.
  const roomOccupantsForPayment = useMemo(() => {
    // BUG FIX: this required paymentForm.roomId to be set before returning
    // ANY candidates at all — correct for HMO, but a BTL/Single Let
    // property has no room to select in the first place (removed earlier
    // today), so paymentForm.roomId can never be set for one. The tenant
    // dropdown was therefore permanently empty for every non-HMO property,
    // not just incomplete. property is available immediately (a resolved
    // prop, not fetched), so checked directly here rather than depending
    // on the isHMO constant declared later in this file's execution order.
    const propertyIsHMO = (property.property_type || property.propertyType || 'hmo') === 'hmo';
    if (propertyIsHMO && !paymentForm.roomId) return [];
    const roomTenants = propertyIsHMO
      ? propTenants.filter(t => String(t.room_id) === String(paymentForm.roomId))
      : propTenants.filter(t => String(t.property_id || t.propertyId) === String(property.id));
    if (!paymentForm.month) return roomTenants;
    const monthStart = `${paymentForm.month}-01`;
    const monthEnd = new Date(new Date(monthStart).getFullYear(), new Date(monthStart).getMonth()+1, 0).toISOString().substring(0,10);
    // BUG FIX: A tenant record with no lease_start recorded was defaulting
    // to '0000-01-01' — meaning they'd incorrectly match EVERY historical
    // month, not just the ones they actually lived there. With two "valid"
    // matches instead of one, auto-select never fired, and an unpicked
    // payment silently saved with no tenant link — which then displayed
    // the room's current occupant instead. Fixed with two passes: first
    // try only tenants with a real recorded lease_start (precise), and
    // only fall back to including those without one if that yields
    // nothing at all — so incomplete data never creates false ambiguity
    // when a precise answer is available.
    const matches = (candidates) => candidates.filter(t => {
      const leaseStart = t.lease_start ? t.lease_start.substring(0,10) : null;
      if (!leaseStart) return false;
      // BUG FIX: same root cause as the payment-attribution and arrears
      // fixes elsewhere today — this defaulted to year 9999 whenever
      // lease_end wasn't set, so a tenant who'd moved out (move_out_date
      // set, but no separate lease_end) kept matching every month
      // indefinitely. That risked this picker auto-selecting or offering
      // an already-departed tenant when recording a NEW payment — which
      // would create a fresh instance of the exact misattribution bug this
      // whole fix pass exists to close, rather than just displaying one.
      const candidateEnds = [t.lease_end, t.move_out_date].filter(Boolean).map(d => d.substring(0,10));
      const leaseEnd = candidateEnds.length ? candidateEnds.sort()[0] : '9999-12-31';
      return leaseStart <= monthEnd && monthStart <= leaseEnd;
    });
    const precise = matches(roomTenants);
    if (precise.length > 0) return precise;
    // No tenant has a usable lease_start for this room at all — fall back
    // to anyone linked to the room rather than leaving the picker empty.
    return roomTenants;
  }, [paymentForm.roomId, paymentForm.month, propTenants]);

  useEffect(() => {
    if (roomOccupantsForPayment.length === 1 && !paymentForm.tenantId) {
      setPaymentForm(p => ({ ...p, tenantId: String(roomOccupantsForPayment[0].id) }));
    }
  }, [roomOccupantsForPayment]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [viewTenant, setViewTenant] = useState(null);
  const [tenantTab, setTenantTab] = useState('details');
  const [tenantPayments, setTenantPayments] = useState([]);
  const [showAgencyEdit, setShowAgencyEdit] = useState(false);
  const [agencyForm, setAgencyForm] = useState({
    management_type: property.management_type || 'self',
    agency_name: property.agency_name || '',
    agency_contact: property.agency_contact || '',
    agency_fee_percent: property.agency_fee_percent || '',
    managing_contractor_id: property.managingContractorId || property.managing_contractor_id || null,
    jurisdiction: property.jurisdiction || 'england',
  });

  // Service requests state
  const [propServiceRequests, setPropServiceRequests] = useState([]);
  const [srLoading, setSrLoading] = useState(false);
  const [selectedSR, setSelectedSR] = useState(null);

  // Licence modals
  const [showLicenceModal, setShowLicenceModal] = useState(false);
  const [collapsedPayYears, setCollapsedPayYears] = useState({});
  const [collapsedExpYears, setCollapsedExpYears] = useState({});
  const togglePayYear = (yr) => setCollapsedPayYears(p => ({ ...p, [yr]: !p[yr] }));
  const toggleExpYear = (yr) => setCollapsedExpYears(p => ({ ...p, [yr]: !p[yr] }));
  const [editLicence, setEditLicence] = useState(null);
  const [licenceForm, setLicenceForm] = useState(defaultLicenceForm);
  const [pendingFile, setPendingFile] = useState(null); // file selected in the Add Document modal
  const [scanningDoc, setScanningDoc] = useState(false);
  const [scanDocError, setScanDocError] = useState('');
  const [scanDocSuccess, setScanDocSuccess] = useState(false);

  // AI-004: Batch scan multiple licences/certificates at once — same
  // scan-all-then-review-together pattern used for receipts in Transactions.
  const [showBatchScan, setShowBatchScan] = useState(false);
  const [batchStep, setBatchStep] = useState('upload'); // 'upload' | 'scanning' | 'reviewing'
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const [batchDragActive, setBatchDragActive] = useState(false);
  const [batchError, setBatchError] = useState('');
  const [batchFileErrors, setBatchFileErrors] = useState([]);
  const [batchReviewItems, setBatchReviewItems] = useState([]);
  const [batchPreviews, setBatchPreviews] = useState({}); // index -> { url, isPdf, name }
  const [batchSaving, setBatchSaving] = useState(false);
  const [batchAddedLog, setBatchAddedLog] = useState([]);

  // New-version modal (replaces window.prompt for expiry date entry)
  const [versionModal, setVersionModal] = useState(null); // { lic, file, expiry }

  // ── DOC-FE: property documents (files attached to licences via doc_group) ──
  const [propertyDocs, setPropertyDocs] = useState([]);
  const [uploadingFor, setUploadingFor] = useState(null);   // licence id currently uploading
  const [expandedHistory, setExpandedHistory] = useState({}); // { [docGroup]: bool }
  const [storageInfo, setStorageInfo] = useState(null);
  const DOC_API = API_URL;

  const loadDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${DOC_API}/api/properties/${property.id}/documents`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setPropertyDocs(await res.json());
      const sRes = await fetch(`${DOC_API}/api/storage/usage`, { headers: { Authorization: `Bearer ${token}` } });
      if (sRes.ok) setStorageInfo(await sRes.json());
    } catch (e) { /* non-fatal */ }
  };

  useEffect(() => { if (property?.id) loadDocuments(); /* eslint-disable-next-line */ }, [property?.id]);

  // Documents for a given licence, newest version first
  const docsForLicence = (licenceId) => propertyDocs
    .filter(d => d.doc_group === `licence-${licenceId}`)
    .sort((a, b) => b.version_number - a.version_number);
  const currentDocForLicence = (licenceId) => docsForLicence(licenceId).find(d => d.is_current);

  // Entry point from UI. For a compliance renewal, open the date-picker modal first.
  // AI-004: Scan the already-picked file to pre-fill the form fields below —
  // the actual upload still happens through the normal "Add Document" submit,
  // this just saves typing in the type/reference/dates by hand.
  const handleScanDocument = async () => {
    if (!pendingFile) return;
    setScanningDoc(true);
    setScanDocError('');
    setScanDocSuccess(false);
    try {
      const extracted = await documentsAPI.scan(pendingFile);
      setLicenceForm(p => ({
        ...p,
        type: extracted.docType || p.type,
        referenceNumber: extracted.referenceNumber || p.referenceNumber,
        validFrom: extracted.issueDate || p.validFrom,
        validTo: extracted.expiryDate || p.validTo,
        description: extracted.description || p.description,
      }));
      setScanDocSuccess(true);
      setTimeout(() => setScanDocSuccess(false), 4000);
    } catch (err) { setScanDocError(err.error || 'Failed to scan document — please fill in the details manually.'); }
    finally { setScanningDoc(false); }
  };

  // AI-004: Batch scan — open, scan-all, review-all, save-all handlers
  const openBatchScan = () => {
    Object.values(batchPreviews).forEach(p => { try { URL.revokeObjectURL(p.url); } catch {} });
    setBatchStep('upload');
    setBatchFiles([]);
    setBatchProgress({ current: 0, total: 0 });
    setBatchDragActive(false);
    setBatchError('');
    setBatchFileErrors([]);
    setBatchReviewItems([]);
    setBatchPreviews({});
    setBatchAddedLog([]);
    setShowBatchScan(true);
  };

  const handleBatchScanSubmit = async () => {
    if (!batchFiles.length) return setBatchError('Please choose at least one document');
    setBatchStep('scanning');
    setBatchError('');
    setBatchFileErrors([]);
    const rows = [];
    const previews = {};
    const fileErrors = [];

    for (let i = 0; i < batchFiles.length; i++) {
      setBatchProgress({ current: i + 1, total: batchFiles.length });
      try {
        const extracted = await documentsAPI.scan(batchFiles[i]);
        previews[i] = {
          url: URL.createObjectURL(batchFiles[i]),
          isPdf: batchFiles[i].type === 'application/pdf',
          name: batchFiles[i].name,
        };
        rows.push({
          include: true,
          fileIndex: i,
          type: extracted.docType || COMPLIANCE_TYPES[0],
          referenceNumber: extracted.referenceNumber || '',
          validFrom: extracted.issueDate || '',
          validTo: extracted.expiryDate || '',
          description: extracted.description || '',
        });
      } catch (err) {
        fileErrors.push({ file: batchFiles[i].name, error: err.error || 'Failed to scan' });
      }
    }

    setBatchPreviews(previews);
    setBatchFileErrors(fileErrors);
    setBatchReviewItems(rows);
    if (!rows.length) {
      setBatchError('Nothing could be read from the file(s) selected. You can still add these manually.');
      setBatchStep('upload');
    } else {
      setBatchStep('reviewing');
    }
  };

  const updateBatchItem = (idx, patch) => {
    setBatchReviewItems(prev => prev.map((item, i) => i === idx ? { ...item, ...patch } : item));
  };

  // Saves every reviewed document in one action: builds all the new licence
  // records and saves them to the property in a single update (rather than
  // one API call per document), then uploads each file against its own new
  // record. Always treated as brand-new documents, not renewals — the
  // existing single-document renewal-versioning flow is unaffected.
  const handleBatchSave = async () => {
    setBatchError('');
    const included = batchReviewItems.filter(r => r.include);
    if (!included.length) return setBatchError('Select at least one document to save');
    for (const r of included) {
      if (!r.type) return setBatchError('Every included document needs a type selected');
    }
    setBatchSaving(true);
    try {
      const newLicences = included.map(r => {
        const isCompliance = isComplianceType(r.type);
        return {
          id: `lic_${Date.now()}_${r.fileIndex}`,
          type: r.type,
          referenceNumber: r.referenceNumber,
          validFrom: r.validFrom,
          validTo: isCompliance ? r.validTo : '',
          description: r.description,
          createdAt: new Date().toISOString(),
          archived: false,
        };
      });

      const cd = property.capitalData || property.capital_data || {};
      const updatedLicences = [...licences, ...newLicences];
      const result = await propertiesAPI.update(property.id, { ...property, licences: updatedLicences, capitalData: cd });
      onUpdate(result);

      const token = localStorage.getItem('token');
      const log = [];
      for (let j = 0; j < included.length; j++) {
        const row = included[j];
        const lic = newLicences[j];
        const file = batchFiles[row.fileIndex];
        try {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('docType', lic.type);
          fd.append('docGroup', `licence-${lic.id}`);
          if (lic.referenceNumber) fd.append('referenceNumber', lic.referenceNumber);
          if (lic.validFrom) fd.append('issueDate', lic.validFrom);
          if (lic.validTo) fd.append('expiryDate', lic.validTo);
          const res = await fetch(`${DOC_API}/api/properties/${property.id}/documents`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Upload failed');

          // Calendar reminder for compliance docs with an expiry, matching
          // the existing single-document flow.
          if (isComplianceType(lic.type) && lic.validTo) {
            try {
              const expiryDate = new Date(lic.validTo);
              const reminderDate = new Date(expiryDate);
              reminderDate.setDate(reminderDate.getDate() - 30);
              const calTask = { id:`licence-${lic.id}`, date:reminderDate.toISOString().split('T')[0], title:`${lic.type} Expiring Soon - ${property.address}`, description:`Expires ${expiryDate.toLocaleDateString('en-GB')}. Ref: ${lic.referenceNumber||'N/A'}`, type:'licence-expiry', color:'#f59e0b', completed:false, propertyId:property.id, licenceId:lic.id };
              const createdTask = await calendarAPI.create(calTask);
              setCalendarTasks(prev => [createdTask, ...prev.filter(t => t.id !== createdTask.id && t.id !== calTask.id)]);
            } catch (e) { console.warn('Reminder creation failed:', e); }
          }
          log.push(`✅ ${lic.type}${lic.referenceNumber ? ' — ' + lic.referenceNumber : ''}`);
        } catch (err) {
          log.push(`❌ ${lic.type} — failed to upload: ${err.message}`);
        }
      }
      setBatchAddedLog(log);
      setBatchStep('done');
    } catch (err) { setBatchError(err.error || err.message || 'Failed to save documents'); }
    finally { setBatchSaving(false); }
  };

  const uploadDocForLicence = async (lic, file, skipPrompt = false) => {
    if (!file) return;
    const compliance = isComplianceType(lic.type);
    const isRenewal = !skipPrompt && docsForLicence(lic.id).length > 0;
    if (isRenewal && compliance) {
      setVersionModal({ lic, file, expiry: lic.validTo || '' });
      return;
    }
    await doUpload(lic, file, lic.validTo || '', false);
  };

  // The actual upload. newExpiry is the (possibly updated) expiry to store.
  // refreshReminder=true means update the licence record + calendar reminder.
  const doUpload = async (lic, file, newExpiry, refreshReminder) => {
    const compliance = isComplianceType(lic.type);
    setUploadingFor(lic.id);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('docType', lic.type || 'Certificate');
      fd.append('docGroup', `licence-${lic.id}`);
      if (lic.referenceNumber) fd.append('referenceNumber', lic.referenceNumber);
      if (lic.validFrom) fd.append('issueDate', lic.validFrom);
      if (newExpiry)     fd.append('expiryDate', newExpiry);
      const res = await fetch(`${DOC_API}/api/properties/${property.id}/documents`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');

      // Update licence expiry if it changed
      if (compliance && refreshReminder && newExpiry && newExpiry !== (lic.validTo || '')) {
        try {
          const cd = property.capitalData || property.capital_data || {};
          const updatedLicences = licences.map(l => l.id === lic.id ? { ...l, validTo: newExpiry } : l);
          const result = await propertiesAPI.update(property.id, { ...property, licences: updatedLicences, capitalData: cd });
          onUpdate(result);
        } catch (e) { console.warn('Licence expiry update failed:', e); }
      }

      // Always (re)create the calendar reminder for a compliance doc that has an expiry.
      // This is the fix: previously the reminder only fired when the date changed.
      const effectiveExpiry = newExpiry || lic.validTo;
      if (compliance && effectiveExpiry) {
        try {
          const expiryDate = new Date(effectiveExpiry);
          const reminderDate = new Date(expiryDate);
          reminderDate.setDate(reminderDate.getDate() - 30);
          const calTask = { id:`licence-${lic.id}`, date:reminderDate.toISOString().split('T')[0], title:`${lic.type} Expiring Soon - ${property.address}`, description:`Expires ${expiryDate.toLocaleDateString('en-GB')}. Ref: ${lic.referenceNumber||'N/A'}`, type:'licence-expiry', color:'#f59e0b', completed:false, propertyId:property.id, licenceId:lic.id };
          const r = await calendarAPI.create(calTask);
          setCalendarTasks(prev => [r, ...prev.filter(t => t.id !== r.id && t.id !== `licence-${lic.id}`)]);
        } catch (e) { console.warn('Reminder refresh failed:', e); }
      }

      await loadDocuments();
    } catch (e) {
      setError(e.message || 'Failed to upload document');
    } finally {
      setUploadingFor(null);
    }
  };

  const downloadDoc = async (docId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${DOC_API}/api/documents/${docId}/download`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Download failed');
      window.open(data.url, '_blank');
    } catch (e) { setError(e.message || 'Failed to download'); }
  };

  const deleteSingleDoc = async (docId, isCurrent, versionCount) => {
    const msg = versionCount > 1
      ? (isCurrent ? 'Delete this version? The previous version will become current.' : 'Delete this version?')
      : 'Delete this document? This cannot be undone.';
    if (!window.confirm(msg)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${DOC_API}/api/documents/${docId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Delete failed'); }
      await loadDocuments();
    } catch (e) { setError(e.message || 'Failed to delete document'); }
  };

  const deleteAllVersions = async (docId, count) => {
    if (!window.confirm(`Delete this document and ALL ${count} version${count!==1?'s':''}? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${DOC_API}/api/documents/${docId}?group=true`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Delete failed'); }
      await loadDocuments();
    } catch (e) { setError(e.message || 'Failed to delete document'); }
  };

  const fmtBytes = (b) => b == null ? '' : b < 1024*1024 ? `${(b/1024).toFixed(0)} KB` : `${(b/1024/1024).toFixed(1)} MB`;

  // ── Photo sets (galleries) ──
  const [showSetModal, setShowSetModal] = useState(false);
  const [newSetName, setNewSetName] = useState('');
  const [uploadingSet, setUploadingSet] = useState(null); // doc_group being uploaded to
  const [lightbox, setLightbox] = useState(null);         // { url, caption }

  // Group gallery images by their set (doc_group), preserving sort order
  const photoSets = useMemo(() => {
    const galleryDocs = propertyDocs.filter(d => d.is_gallery);
    const sets = {};
    for (const d of galleryDocs) {
      if (!sets[d.doc_group]) sets[d.doc_group] = { group: d.doc_group, name: d.set_name || 'Photos', images: [] };
      sets[d.doc_group].images.push(d);
    }
    Object.values(sets).forEach(s => s.images.sort((a,b) => (a.sort_order||0) - (b.sort_order||0)));
    return Object.values(sets);
  }, [propertyDocs]);

  const createPhotoSet = async (name, files) => {
    if (!name.trim()) { setError('Give the photo set a name'); return; }
    const group = `photoset-${property.id}-${Date.now()}`;
    setUploadingSet(group);
    setError('');
    try {
      for (const file of files) {
        await uploadGalleryImage(group, name.trim(), file);
      }
      await loadDocuments();
      setShowSetModal(false);
      setNewSetName('');
    } catch (e) { setError(e.message || 'Failed to create photo set'); }
    finally { setUploadingSet(null); }
  };

  const uploadGalleryImage = async (group, setName, file) => {
    const token = localStorage.getItem('token');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('docType', 'Photos');
    fd.append('docGroup', group);
    fd.append('isGallery', 'true');
    fd.append('setName', setName);
    const res = await fetch(`${DOC_API}/api/properties/${property.id}/documents`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
    });
    if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Upload failed'); }
  };

  const addToPhotoSet = async (group, setName, files) => {
    setUploadingSet(group);
    setError('');
    try {
      for (const file of files) await uploadGalleryImage(group, setName, file);
      await loadDocuments();
    } catch (e) { setError(e.message || 'Failed to add photos'); }
    finally { setUploadingSet(null); }
  };

  const deleteGalleryImage = async (docId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${DOC_API}/api/documents/${docId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Delete failed'); }
      await loadDocuments();
    } catch (e) { setError(e.message || 'Failed to delete photo'); }
  };

  const deletePhotoSet = async (group, count) => {
    if (!window.confirm(`Delete this photo set and all ${count} photo${count!==1?'s':''}? This cannot be undone.`)) return;
    const first = propertyDocs.find(d => d.doc_group === group);
    if (!first) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${DOC_API}/api/documents/${first.id}?group=true`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Delete failed'); }
      await loadDocuments();
    } catch (e) { setError(e.message || 'Failed to delete photo set'); }
  };

  // Small component: a gallery image tile (fetches its own signed URL)
  const GalleryTile = ({ doc }) => {
    const [url, setUrl] = useState(null);
    useEffect(() => {
      let alive = true;
      (async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${DOC_API}/api/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
          const data = await res.json();
          if (alive && res.ok) setUrl(data.url);
        } catch {}
      })();
      return () => { alive = false; };
      // eslint-disable-next-line
    }, [doc.id]);
    return (
      <div style={{ position:'relative', borderRadius:'0.5rem', overflow:'hidden', aspectRatio:'1', background:'#f1f5f9', cursor: url ? 'pointer' : 'default' }}
           onClick={() => url && setLightbox({ url, caption: doc.caption || doc.filename })}>
        {url
          ? <img src={url} alt={doc.caption||''} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          : <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', fontSize:'1.5rem' }}>🖼️</div>}
        <button onClick={(e) => { e.stopPropagation(); deleteGalleryImage(doc.id); }}
          style={{ position:'absolute', top:'4px', right:'4px', background:'rgba(0,0,0,0.6)', color:'#fff', border:'none', borderRadius:'50%', width:'22px', height:'22px', cursor:'pointer', fontSize:'0.7rem', lineHeight:1 }}
          title="Delete photo">×</button>
      </div>
    );
  };

  // Thumbnail: shows an image preview (via signed URL) or a file-type icon
  const DocThumb = ({ doc, size = 150 }) => {
    const [imgUrl, setImgUrl] = useState(null);
    const isImg = isImageFile(doc.mime_type, doc.filename);
    useEffect(() => {
      let alive = true;
      if (isImg) {
        (async () => {
          try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${DOC_API}/api/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (alive && res.ok) setImgUrl(data.url);
          } catch {}
        })();
      }
      return () => { alive = false; };
      // eslint-disable-next-line
    }, [doc.id]);
    const icon = /pdf$/i.test(doc.filename) ? '📄'
      : /\.(xlsx?|csv)$/i.test(doc.filename) ? '📊'
      : /\.(docx?)$/i.test(doc.filename) ? '📝'
      : /\.(pptx?)$/i.test(doc.filename) ? '📽️' : '📎';
    return (
      <div style={{ width:size, height:size, borderRadius:'0.375rem', overflow:'hidden', flexShrink:0, background:'#f1f5f9', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid var(--border)' }}>
        {isImg && imgUrl
          ? <img src={imgUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          : <span style={{ fontSize:size*0.5 }}>{icon}</span>}
      </div>
    );
  };

  const rooms = property.rooms || [];
  // BUG FIX: needed at the top level to gate the Rooms tab itself and its
  // default-tab behaviour — was previously only computed inline, further
  // down, inside a single tab's own render block, so nothing outside that
  // block could use it. A Single Let / BTL property has no rooms by
  // definition; matches the same 'hmo'-vs-everything-else convention
  // already used consistently in server.js/Reports.js/Tenants.js.
  const isHMO = (property.property_type || property.propertyType || 'hmo') === 'hmo';
  const capitalData = property.capital_data || property.capitalData || {};
  const licences = property.licences || [];
  const currentMonth = getCurrentMonth();
  const cur = property.currency || 'GBP';
  const curSymbol = symbolFor(cur);
  // Currency-aware formatter for this property
  const fmtCur = (amount) => formatCurrency(amount, cur);

  // Fix: handle both snake_case and camelCase IDs from API
  const propPayments = useMemo(() =>
    payments.filter(p => String(p.propertyId||p.property_id) === String(property.id)),
    [payments, property.id]
  );
  const propExpenses = useMemo(() =>
    expenses.filter(e => String(e.propertyId||e.property_id) === String(property.id)),
    [expenses, property.id]
  );

  // ===== ROOM HELPERS =====
  const saveRooms = async (updatedRooms) => {
    setLoading(true);
    setError('');
    try {
      const cd = property.capitalData || property.capital_data || {};
      const result = await propertiesAPI.update(property.id, { ...property, rooms: updatedRooms, capitalData: cd });
      onUpdate(result);
      return true;
    } catch (err) {
      setError(err.error || 'Failed to update. Please try again.');
      return false;
    } finally { setLoading(false); }
  };

  // ===== ADD ROOM =====
  const handleAddRoom = async (e) => {
    e.preventDefault();
    const room = {
      id: `room_${Date.now()}_${Math.random().toString(36).substr(2,9)}`,
      roomName: newRoomForm.roomName,
      tenantName: newRoomForm.tenantName,
      rentAmount: parseFloat(newRoomForm.rentAmount) || 0,
      paymentDueDay: parseInt(newRoomForm.paymentDueDay) || 1,
      status: newRoomForm.status === 'Occupied' ? 'occupied' : 'vacant',
      leaseStart: newRoomForm.leaseStart || null,
      leaseEnd: newRoomForm.leaseEnd || null,
      vacantSince: newRoomForm.status !== 'Occupied' ? new Date().toISOString().split('T')[0] : null
    };
    const ok = await saveRooms([...rooms, room]);
    if (ok) { setShowAddRoomModal(false); setNewRoomForm({ roomName:'', tenantName:'', rentAmount:'', paymentDueDay:1, status:'Vacant', leaseStart:'', leaseEnd:'' }); }
  };

  // ===== EDIT ROOM =====
  const handleUpdateRoom = async (updatedRoom) => {
    const updatedRooms = rooms.map(r => r.id === updatedRoom.id ? { ...updatedRoom, status: updatedRoom.status?.toLowerCase() } : r);
    const ok = await saveRooms(updatedRooms);
    if (ok) setEditingRoom(null);
  };

  // ===== MARK VACANT =====
  const handleMarkVacant = async () => {
    const updatedRooms = rooms.map(r => r.id === vacantRoom.id
      ? { ...r, status:'vacant', vacantSince:vacantForm.date, vacantNotes:vacantForm.notes, tenantName:'' } : r
    );
    const ok = await saveRooms(updatedRooms);
    if (ok) setShowVacantModal(false);
  };

  // ===== MARK OCCUPIED =====
  const handleMarkOccupied = async (room, tenantName, rentAmount) => {
    const updatedRooms = rooms.map(r => r.id === room.id
      ? { ...r, status:'occupied', tenantName, rentAmount: parseFloat(rentAmount)||r.rentAmount, vacantSince:null } : r
    );
    await saveRooms(updatedRooms);
  };

  // ===== PAYMENT ACTIONS =====
  const handleAddPayment = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (editingPayment) {
        // BUG FIX: There was previously no way to correct a payment's
        // recorded month at all — needed so a mis-assigned catch-up payment
        // (e.g. a double payment recorded against the wrong month) can be
        // reassigned to the month it actually settles, which the Arrears
        // Report's month-by-month check relies on being accurate.
        const result = await paymentsAPI.update(editingPayment.id, {
          propertyId: property.id,
          roomId: paymentForm.roomId,
          month: paymentForm.month,
          amount: parseFloat(paymentForm.amount),
          paidDate: paymentForm.paidDate,
          tenantId: paymentForm.tenantId || null,
        });
        setPayments(prev => prev.map(p => p.id === editingPayment.id ? result : p));
        setShowPaymentModal(false);
        setEditingPayment(null);
        setLoading(false);
        return;
      }
      const result = await paymentsAPI.create({
        propertyId: property.id,
        roomId: paymentForm.roomId,
        month: paymentForm.month,
        amount: parseFloat(paymentForm.amount),
        paidDate: paymentForm.paidDate,
        tenantId: paymentForm.tenantId || null,
      });
      setPayments(prev => [result, ...prev]);
      // DUPE-001: Non-blocking heads-up if another payment already exists for
      // this property/room/month — could be a genuine split payment (some
      // tenants pay part at the start and end of the month) or an accidental
      // duplicate from another tab/team member. Never blocks — just a nudge.
      if (result.duplicateWarning) {
        const sym = symbolFor(property.currency);
        const dw = result.duplicateWarning;
        setPaymentWarning(
          `⚠️ Heads up — this is payment #${dw.priorPaymentCount + 1} recorded for this room this month (previous total ${sym}${dw.priorTotalAmount.toFixed(2)}, now ${sym}${dw.newTotalAmount.toFixed(2)}). If this was a split payment that's fine — if it was accidental, you can delete the duplicate from the Payments tab.`
        );
        setTimeout(() => setPaymentWarning(null), 12000);
      }
      // Quick expenses: save all valid rows
      if (addExpenseAfterPayment) {
        for (const qe of quickExpenses) {
          if (qe.category && qe.amount) {
            try {
              const expResult = await expensesAPI.create({
                propertyId: property.id,
                category: qe.category,
                amount: parseFloat(qe.amount),
                date: paymentForm.paidDate,
                description: qe.description || '',
                paid: qe.paid
              });
              setExpenses(prev => [expResult, ...prev]);
            } catch (expErr) { console.warn('Quick expense failed (non-fatal):', expErr); }
          }
        }
      }
      setShowPaymentModal(false);
      setPaymentForm({ roomId:'', month:getCurrentMonth(), amount:'', paidDate:new Date().toISOString().split('T')[0] });
      setAddExpenseAfterPayment(false);
      setQuickExpenses([{ category:'', amount:'', description:'', paid:true }]);

      // Mark matching rent-due calendar tasks as paid/completed
      // Tasks are keyed as "rent-YYYY-MM-roomId" or "rent-due-YYYY-MM-roomId"
      try {
        const [yr, mo] = (paymentForm.month || '').split('-');
        const matchingTasks = calendarTasks.filter(t => {
          if (t.completed || t.paid) return false;
          if (t.type !== 'rent' && t.type !== 'rent-due') return false;
          const taskDate = (t.date || '').substring(0, 7); // YYYY-MM
          const taskMonth = `${yr}-${mo}`;
          const roomMatch = String(t.room_id || t.roomId || '') === String(paymentForm.roomId);
          const propMatch = String(t.property_id || t.propertyId || '') === String(property.id);
          const monthMatch = taskDate === taskMonth;
          return propMatch && (roomMatch || monthMatch);
        });
        for (const task of matchingTasks) {
          try {
            const updated = await calendarAPI.update(task.id, {
              ...task,
              date: (task.date || '').substring(0, 10),
              completed: true,
              paid: true
            });
            setCalendarTasks(prev => prev.map(t => t.id === task.id ? updated : t));
          } catch (calErr) { console.warn('Could not mark calendar task complete:', calErr); }
        }
      } catch (calErr) { console.warn('Calendar sync error:', calErr); }

    } catch (err) { setError(err.error || 'Failed to record payment. Check all fields.'); }
    finally { setLoading(false); }
  };

  const openEditPayment = (p) => {
    setEditingPayment(p);
    setPaymentForm({
      roomId: String(p.roomId || p.room_id || ''),
      month: p.month || getCurrentMonth(),
      amount: String(p.amount || ''),
      paidDate: (p.paidDate || p.paid_date || '').substring(0,10) || new Date().toISOString().split('T')[0],
      tenantId: p.tenantId || p.tenant_id || '',
    });
    setError('');
    if (!propTenants.length) loadPropTenants();
    setShowPaymentModal(true);
  };

  const handleDeletePayment = async (id) => {
    if (!window.confirm('Delete this payment?')) return;
    try { await paymentsAPI.delete(id); setPayments(prev => prev.filter(p => p.id !== id)); }
    catch (err) { alert(err.error || 'Failed to delete'); }
  };

  // ===== EXPENSE ACTIONS =====
  const handleAddExpense = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await expensesAPI.create({
        propertyId: property.id,
        category: expenseForm.category,
        amount: parseFloat(expenseForm.amount),
        date: expenseForm.date,
        description: expenseForm.description,
        paid: expenseForm.paid
      });
      setExpenses(prev => [result, ...prev]);
      setShowExpenseModal(false);
      setExpenseForm({ category:'', amount:'', date:new Date().toISOString().split('T')[0], description:'', paid:false });
    } catch (err) { setError(err.error || 'Failed to add expense. Check all fields.'); }
    finally { setLoading(false); }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await expensesAPI.delete(id); setExpenses(prev => prev.filter(e => e.id !== id)); }
    catch (err) { alert(err.error || 'Failed to delete'); }
  };

  const handleToggleExpensePaid = async (exp) => {
    try {
      const result = await expensesAPI.update(exp.id, { paid: !exp.paid });
      setExpenses(prev => prev.map(e => e.id === exp.id ? result : e));
    } catch (err) { alert(err.error || 'Failed to update'); }
  };

  // ===== LICENCE / DOCUMENT ACTIONS =====
  const handleAddLicence = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const licenceId = editLicence ? editLicence.id : `lic_${Date.now()}`;
      const isCompliance = isComplianceType(licenceForm.type);
      // General/photo types don't carry expiry — clear it so no reminder is created
      const formToSave = isCompliance ? licenceForm : { ...licenceForm, validTo: '' };
      const newLicence = { ...formToSave, id: licenceId, createdAt: new Date().toISOString(), archived: false };
      const updatedLicences = editLicence
        ? licences.map(l => l.id === editLicence.id ? { ...l, ...formToSave } : l)
        : [...licences, newLicence];
      const cd = property.capitalData || property.capital_data || {};
      const result = await propertiesAPI.update(property.id, { ...property, licences: updatedLicences, capitalData: cd });
      onUpdate(result);

      // Create calendar reminder 30 days before expiry — compliance types only, new only
      if (!editLicence && isCompliance && licenceForm.validTo) {
        try {
          const expiryDate = new Date(licenceForm.validTo);
          const reminderDate = new Date(expiryDate);
          reminderDate.setDate(reminderDate.getDate() - 30);
          const calTask = { id: `licence-${licenceId}`, date: reminderDate.toISOString().split('T')[0], title: `${licenceForm.type} Expiring Soon - ${property.address}`, description: `Expires ${expiryDate.toLocaleDateString('en-GB')}. Ref: ${licenceForm.referenceNumber||'N/A'}`, type: 'licence-expiry', color: '#f59e0b', completed: false, propertyId: property.id, licenceId };
          const createdTask = await calendarAPI.create(calTask);
          setCalendarTasks(prev => [createdTask, ...prev.filter(t => t.id !== createdTask.id && t.id !== calTask.id)]);
        } catch (calErr) { console.warn('Calendar reminder failed:', calErr); }
      }

      // Upload the file attached at creation, if any (new records only)
      if (!editLicence && pendingFile) {
        const licObj = { id: licenceId, type: licenceForm.type, referenceNumber: licenceForm.referenceNumber, validFrom: licenceForm.validFrom, validTo: isCompliance ? licenceForm.validTo : '' };
        await uploadDocForLicence(licObj, pendingFile, true); // skipPrompt = true (fresh upload, not a renewal)
      }

      setShowLicenceModal(false);
      setEditLicence(null);
      setLicenceForm(defaultLicenceForm);
      setPendingFile(null);
    } catch (err) { setError(err.error || 'Failed to save licence.'); }
    finally { setLoading(false); }
  };

  const handleDeleteLicence = async (licId) => {
    if (!window.confirm('Delete this licence?')) return;
    const updatedLicences = licences.filter(l => l.id !== licId);
    const cd = property.capitalData || property.capital_data || {};
    try {
      const result = await propertiesAPI.update(property.id, { ...property, licences: updatedLicences, capitalData: cd });
      onUpdate(result);
    } catch (err) { alert(err.error || 'Failed to delete'); }
  };

  const openEditLicence = (lic) => {
    setLicenceForm({ type:lic.type||'', description:lic.description||'', referenceNumber:lic.referenceNumber||'', validFrom:lic.validFrom||'', validTo:lic.validTo||'', notes:lic.notes||'' });
    setEditLicence(lic);
    setShowLicenceModal(true);
  };

  // ===== CAPITAL CALCS =====
  const equity = parseFloat(capitalData.currentValue||0) - parseFloat(capitalData.mortgageBalance||0);
  // Load service requests for this property
  const loadPropServiceRequests = async () => {
    setSrLoading(true);
    try {
      const all = await serviceRequestsAPI.getAll();
      setPropServiceRequests(all.filter(r => String(r.property_id) === String(property.id)));
    } catch (e) { console.warn('Could not load service requests:', e); }
    finally { setSrLoading(false); }
  };

  // Load tenants for this property when tenants tab is opened
  const loadPropTenants = async () => {
    setTenantsLoading(true);
    try {
      const all = await tenantsAPI.getAll();
      setPropTenants(all.filter(t => String(t.property_id) === String(property.id)));
    } catch (e) { console.warn('Could not load property tenants:', e); }
    finally { setTenantsLoading(false); }
  };

  // Open tenant view with payment history
  const openTenantView = async (t) => {
    setViewTenant(t);
    setTenantTab('details');
    try {
      const pmts = await tenantsAPI.getPayments(t.id);
      setTenantPayments(pmts);
    } catch (e) { setTenantPayments([]); }
  };

  // Save agency/management settings
  const handleSaveAgency = async () => {
    try {
      const updated = { ...property, ...agencyForm };
      const result = await propertiesAPI.update(property.id, updated);
      onUpdate(result);
      setShowAgencyEdit(false);
    } catch (e) { alert('Failed to save management settings'); }
  };

  // BUG FIX: this only ever summed rooms.rentAmount — correct for HMO, but
  // a BTL/Single Let property now has zero rooms (removed earlier today),
  // so this silently computed £0 for every non-HMO property, feeding a
  // wrong Monthly Rent figure and a wrong Gross Yield into the stats below.
  // Monthly rent for a non-HMO property now lives on the tenant's own
  // rent_amount (set via the Tenant module's Lease section) — sourced from
  // there instead. Only active tenants count, matching the existing
  // "occupied" filter used for the HMO case.
  const monthlyRent = isHMO
    ? rooms.filter(r=>r.status==='occupied').reduce((s,r)=>s+parseFloat(r.rentAmount||0),0)
    : propTenants.filter(t=>t.status==='active').reduce((s,t)=>s+parseFloat(t.rent_amount||0),0);
  const annualRent = monthlyRent * 12;
  const grossYield = parseFloat(capitalData.purchasePrice||0) > 0 ? ((annualRent/parseFloat(capitalData.purchasePrice))*100).toFixed(2) : 0;
  const mortBal = parseFloat(capitalData.mortgageBalance||0);
  const mortRate = parseFloat(capitalData.mortgageRate||0);
  // BUG FIX: This field is the mortgage PRODUCT's full term — 20/25/30
  // years, the way every landlord actually thinks about it — not months.
  // The amortization formula needs months, so convert explicitly here
  // rather than storing/labelling the field itself in months, which was
  // confusing enough that values were likely being entered as years into a
  // field labelled months in the first place.
  const mortTermYears = parseInt(capitalData.mortgageTerm||0);
  const mortTermMonths = mortTermYears * 12;
  const isInterestOnly = (capitalData.repaymentType || 'Interest Only') === 'Interest Only';
  const monthlyMortgage = mortBal > 0 && mortRate > 0
    ? (isInterestOnly
        ? (mortBal * (mortRate/100/12)).toFixed(2)
        : (mortTermMonths > 0 ? ((mortBal*(mortRate/100/12))/(1-Math.pow(1+mortRate/100/12,-mortTermMonths))).toFixed(2) : 0))
    : 0;
  // BUG FIX: "Term Remaining" was just echoing the entered total term back
  // unchanged — a static number that never actually moved, despite the
  // label implying a live countdown. Now genuinely calculated from years
  // elapsed since purchase (the closest proxy available for mortgage start
  // — flagged as an estimate since a remortgage could mean the real
  // mortgage start date differs from the purchase date).
  // BUG FIX: Now uses the dedicated mortgage start date when available —
  // purchase date is only a reliable proxy if the mortgage has never
  // changed, which doesn't hold for a remortgage or a term extension.
  // Falls back to purchase date for properties that haven't had this new
  // field filled in yet, so existing estimates don't just disappear.
  const mortgageAnchorDate = capitalData.mortgageStartDate || capitalData.purchaseDate;
  const usingMortgageStartDate = !!capitalData.mortgageStartDate;
  const yearsSinceMortgageStart = mortgageAnchorDate ? Math.max(0, (new Date() - new Date(mortgageAnchorDate + 'T12:00:00')) / (365.25*24*60*60*1000)) : null;
  const termRemainingYears = (mortTermYears > 0 && yearsSinceMortgageStart !== null) ? Math.max(0, Math.round((mortTermYears - yearsSinceMortgageStart) * 10) / 10) : null;

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
        <button className="btn btn-secondary" onClick={onBack}>← Back to Properties</button>
        <div style={{ display:'flex', gap:'0.75rem' }}>
          <button className="btn btn-secondary" onClick={onEdit}>✏️ Edit Property</button>
          <button className="btn btn-danger" onClick={() => onDelete(property.id)}>🗑️ Delete</button>
        </div>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom:'1rem' }}>{error}</div>}
      {paymentWarning && (
        <div style={{ background:'#fef3c7', border:'1px solid #fde047', borderRadius:'0.5rem', padding:'0.85rem 1rem', marginBottom:'1rem', fontSize:'0.875rem', color:'#92400e', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'1rem' }}>
          <span>{paymentWarning}</span>
          <button onClick={() => setPaymentWarning(null)} style={{ background:'none', border:'none', color:'#92400e', cursor:'pointer', fontSize:'1rem', flexShrink:0, lineHeight:1 }}>×</button>
        </div>
      )}

      {/* Property banner */}
      <div className="card" style={{ background:'linear-gradient(135deg, var(--brand-green) 0%, var(--brand-green-light) 100%)', color:'white', marginBottom:'1.5rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'1rem', marginBottom:'0.5rem' }}>
          <h2 style={{ fontSize:'1.5rem', fontWeight:700, margin:0 }}>{property.address}</h2>
          {(() => {
            const pt = property.property_type || property.propertyType || '';
            const typeMap = {
              'hmo': 'HMO', 'HMO': 'HMO',
              'btl': 'BTL', 'BTL': 'BTL',
              'buy-to-let': 'BTL', 'Buy to Let': 'BTL',
              'single-let': 'Single Let', 'Single Let': 'Single Let',
              'commercial': 'Commercial', 'Commercial': 'Commercial',
              'holiday-let': 'Holiday Let', 'Holiday Let': 'Holiday Let',
            };
            const label = typeMap[pt] || pt;
            if (!label) return null;
            return <span style={{ background:'rgba(255,255,255,0.25)', padding:'0.25rem 0.75rem', borderRadius:'1rem', fontSize:'0.75rem', fontWeight:700, whiteSpace:'nowrap', flexShrink:0 }}>{label}</span>;
          })()}
        </div>
        <div style={{ display:'flex', gap:'1.5rem', opacity:0.9, fontSize:'0.875rem', flexWrap:'wrap' }}>
          <span>🏷️ {property.ownership?.toUpperCase()}</span>
          <span>💱 {cur}</span>
          {effectiveJurisdiction(property) !== 'england' && (
            <span>🌍 {{ ireland: 'Ireland', scotland: 'Scotland', wales: 'Wales', northern_ireland: 'N.Ireland' }[effectiveJurisdiction(property)]}</span>
          )}
          {/* BUG FIX: "0 rooms" was always shown for non-HMO properties —
              technically true but meaningless for a type that has no room
              concept at all. Shows active tenant count instead, which is
              actually relevant for that property type. */}
          {isHMO ? (
            <>
              <span>🛏️ {rooms.length} rooms</span>
              <span>✅ {rooms.filter(r=>r.status==='occupied').length} occupied</span>
            </>
          ) : (
            <span>👤 {propTenants.filter(t=>t.status==='active').length} active tenant{propTenants.filter(t=>t.status==='active').length!==1?'s':''}</span>
          )}
          <span>📋 {licences.filter(l=>!l.archived).length} licences</span>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom:'1.5rem' }}>
        <div className="stat-card"><div className="stat-label">Monthly Rent</div><div className="stat-value positive">{fmtCur(monthlyRent)}</div></div>
        <div className="stat-card"><div className="stat-label">This Month Received</div><div className="stat-value positive">{fmtCur(propPayments.filter(p=>p.month===currentMonth).reduce((s,p)=>s+parseFloat(p.amount||0),0))}</div></div>
        <div className="stat-card"><div className="stat-label">Total Expenses Paid</div><div className="stat-value negative">{fmtCur(propExpenses.filter(e=>e.paid===true||e.paid==='true').reduce((s,e)=>s+parseFloat(e.amount||0),0))}</div></div>
        {parseFloat(capitalData.purchasePrice||0) > 0 && <div className="stat-card"><div className="stat-label">Gross Yield</div><div className="stat-value">{grossYield}%</div></div>}
      </div>

      {/* Tabs */}
      <div className="tabs">
        {[...(isHMO ? [['rooms','🛏️ Rooms']] : []),['tenants','👤 Tenants'],['service-requests','🔧 Requests'],['payments','💰 Payments'],['expenses','📤 Expenses'],['capital','📊 Capital'],['licences','📁 Documents'],['management','⚙️ Management']].map(([val,label]) => (
          <button key={val} className={`tab-btn ${activeTab===val?'active':''}`} onClick={() => {
            setActiveTab(val); setError('');
            if (val === 'tenants') loadPropTenants();
            if (val === 'payments' && !propTenants.length) loadPropTenants();
            if (val === 'service-requests') loadPropServiceRequests();
          }}>{label}
          {val === 'service-requests' && propServiceRequests.filter(r => !['completed','closed','cancelled'].includes(r.status)).length > 0 && (
            <span style={{ marginLeft:'0.35rem', background:'var(--danger)', color:'#fff', borderRadius:'0.75rem', padding:'0.1rem 0.4rem', fontSize:'0.65rem', fontWeight:700 }}>
              {propServiceRequests.filter(r => !['completed','closed','cancelled'].includes(r.status)).length}
            </span>
          )}
          </button>
        ))}
      </div>

      {/* ===== AGENCY MANAGEMENT BANNER ===== */}
      {/* ===== TENANTS TAB ===== */}
      {activeTab === 'tenants' && (() => {
        const isHMO = (property.property_type || property.propertyType || 'hmo') === 'hmo';
        const activeTenants = propTenants.filter(t => t.status === 'active');

        // Group non-HMO tenants by tenancy_ref
        const tenancyGroups = {};
        propTenants.forEach(t => {
          const ref = t.tenancy_ref || `single_${t.id}`;
          if (!tenancyGroups[ref]) tenancyGroups[ref] = [];
          tenancyGroups[ref].push(t);
        });
        const tenancies = Object.values(tenancyGroups).map(group =>
          [...group].sort((a, b) => (b.is_lead_tenant ? 1 : 0) - (a.is_lead_tenant ? 1 : 0))
        );

        const navigate_to = (id) => window.location.href = `/tenants/${id}`;

        return (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
            <div>
              <h3 style={{ margin:0 }}>Tenants</h3>
              <p style={{ margin:'0.25rem 0 0', fontSize:'0.875rem', color:'var(--text-secondary)' }}>
                {property.management_type === 'agency' ? `🏢 Managed by ${property.managingContractorName || property.agency_name || 'agency'} — ` : ''}
                {activeTenants.length} active tenant{activeTenants.length !== 1 ? 's' : ''}
                {!isHMO && tenancies.length > 0 && ` · ${tenancies.length} tenancy${tenancies.length !== 1 ? 'ies' : ''}`}
              </p>
            </div>
            <div style={{ display:'flex', gap:'0.5rem' }}>
            </div>
          </div>

          {tenantsLoading ? (
            <div style={{ textAlign:'center', padding:'2rem', color:'var(--text-secondary)' }}>Loading tenants...</div>
          ) : propTenants.length === 0 ? (
            <div className="card"><div className="empty-state"><span>👤</span><h3>No tenants found</h3><p>Tenants linked to this property will appear here</p></div></div>
          ) : isHMO ? (
            /* ── HMO: room-by-room cards ── */
            <div style={{ display:'grid', gap:'0.75rem' }}>
              {propTenants.map(t => {
                const isActive = t.status === 'active';
                const leaseEnd = t.lease_end ? t.lease_end.substring(0,10) : null;
                const daysLeft = leaseEnd ? Math.round((new Date(leaseEnd + 'T12:00:00') - new Date()) / 86400000) : null;
                const leaseColor = !leaseEnd ? 'var(--text-secondary)' : daysLeft < 0 ? 'var(--danger)' : daysLeft <= 30 ? 'var(--danger)' : daysLeft <= 60 ? 'var(--warning)' : 'var(--success)';
                const leaseLabel = !leaseEnd ? 'Periodic' : daysLeft < 0 ? 'Expired' : daysLeft <= 60 ? `Expires in ${daysLeft}d` : leaseEnd;
                const sym = symbolFor(property.currency);
                const roomName = property.rooms?.find(r => String(r.id) === String(t.room_id))?.roomName || t.room_id;
                return (
                  <div key={t.id} className="card" style={{ borderLeft:`4px solid ${isActive ? 'var(--success)' : 'var(--border)'}`, cursor:'pointer' }}
                    onClick={() => navigate_to(t.id)}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                      <div>
                        <div style={{ fontWeight:600, fontSize:'1rem' }}>{t.first_name} {t.last_name}</div>
                        <div style={{ fontSize:'0.8rem', color:'var(--text-secondary)', marginTop:'0.2rem' }}>
                          Room: {roomName}{t.email ? ` · ${t.email}` : ''}{t.phone ? ` · ${t.phone}` : ''}
                        </div>
                        {!isActive && <span style={{ fontSize:'0.7rem', background:'var(--bg-light)', border:'1px solid var(--border)', borderRadius:'0.25rem', padding:'0.1rem 0.4rem', color:'var(--text-secondary)' }}>Moved out</span>}
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontWeight:700, color:'var(--success)' }}>{t.rent_amount ? `${sym}${parseFloat(t.rent_amount).toFixed(2)}/mo` : '—'}</div>
                        <div style={{ fontSize:'0.8rem', color: leaseColor, marginTop:'0.2rem' }}>{leaseLabel}</div>
                      </div>
                    </div>
                    {t.checklist && (() => {
                      const checkKeys = Object.keys(t.checklist);
                      const done = checkKeys.filter(k => t.checklist[k]).length;
                      return checkKeys.length > 0 ? (
                        <div style={{ marginTop:'0.5rem', fontSize:'0.75rem', color: done === checkKeys.length ? 'var(--success)' : 'var(--warning)' }}>
                          Move-in checklist: {done}/{checkKeys.length} {done === checkKeys.length ? '✅' : '⏳'}
                        </div>
                      ) : null;
                    })()}
                  </div>
                );
              })}
            </div>
          ) : (
            /* ── Non-HMO: tenancy cards ── */
            <div style={{ display:'grid', gap:'1rem' }}>
              {tenancies.map((group, gi) => {
                const lead = group.find(t => t.is_lead_tenant) || group[0];
                const joint = group.filter(t => !t.is_lead_tenant && t.status === 'active');
                if (!lead) return null;
                const sym = symbolFor(property.currency);
                const leaseEnd = lead.lease_end ? lead.lease_end.substring(0,10) : null;
                const daysLeft = leaseEnd ? Math.round((new Date(leaseEnd + 'T12:00:00') - new Date()) / 86400000) : null;
                const leaseColor = !leaseEnd ? 'var(--text-secondary)' : daysLeft < 0 ? 'var(--danger)' : daysLeft <= 30 ? 'var(--danger)' : daysLeft <= 60 ? 'var(--warning)' : 'var(--success)';
                const leaseLabel = !leaseEnd ? 'Periodic / Rolling' : daysLeft < 0 ? 'Expired' : daysLeft <= 60 ? `Expires in ${daysLeft}d` : leaseEnd;
                return (
                  <div key={gi} style={{ border:'1px solid var(--border)', borderRadius:'0.5rem', overflow:'hidden' }}>
                    {/* Lead tenant */}
                    <div style={{ padding:'1rem', display:'flex', justifyContent:'space-between', alignItems:'flex-start', cursor:'pointer', background: lead.status === 'active' ? 'transparent' : 'var(--bg-light)' }}
                      onClick={() => navigate_to(lead.id)}>
                      <div>
                        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.25rem' }}>
                          <span style={{ fontWeight:700, fontSize:'1rem' }}>{lead.first_name} {lead.last_name}</span>
                          <span style={{ fontSize:'0.65rem', background:'var(--primary)', color:'#fff', borderRadius:'0.25rem', padding:'0.1rem 0.35rem', fontWeight:600 }}>Lead</span>
                          {lead.status === 'moved_out' && <span style={{ fontSize:'0.65rem', background:'var(--bg-light)', border:'1px solid var(--border)', borderRadius:'0.25rem', padding:'0.1rem 0.35rem', color:'var(--text-secondary)' }}>Moved out</span>}
                        </div>
                        <div style={{ fontSize:'0.8rem', color:'var(--text-secondary)' }}>
                          {lead.email || '—'}{lead.phone ? ` · ${lead.phone}` : ''}
                        </div>
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0, marginLeft:'1rem' }}>
                        <div style={{ fontWeight:700, color:'var(--success)', fontSize:'1.1rem' }}>{lead.rent_amount ? `${sym}${parseFloat(lead.rent_amount).toFixed(2)}/mo` : '—'}</div>
                        <div style={{ fontSize:'0.8rem', color:leaseColor, fontWeight:500, marginTop:'0.15rem' }}>{leaseLabel}</div>
                      </div>
                    </div>
                    {/* Tenancy details */}
                    <div style={{ padding:'0.5rem 1rem', background:'var(--bg-light)', display:'flex', gap:'1.5rem', fontSize:'0.8rem', color:'var(--text-secondary)', flexWrap:'wrap', borderTop:'1px solid var(--border)' }}>
                      {lead.lease_start && <span>📅 From {lead.lease_start.substring(0,10)}</span>}
                      {lead.lease_end ? <span>To {lead.lease_end.substring(0,10)}</span> : <span>Rolling tenancy</span>}
                      {lead.payment_due_day && <span>💰 Due day {lead.payment_due_day}</span>}
                      {lead.deposit_amount && <span>🔒 Deposit {sym}{parseFloat(lead.deposit_amount).toFixed(2)}</span>}
                    </div>
                    {/* Joint tenants */}
                    {joint.length > 0 && (
                      <div style={{ padding:'0.75rem 1rem', borderTop:'1px solid var(--border)' }}>
                        <div style={{ fontSize:'0.7rem', color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.4rem' }}>Joint Tenants</div>
                        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                          {joint.map(jt => (
                            <div key={jt.id} style={{ display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.25rem 0.6rem', background:'var(--bg-light)', border:'1px solid var(--border)', borderRadius:'0.375rem', cursor:'pointer', fontSize:'0.8rem' }}
                              onClick={(e) => { e.stopPropagation(); navigate_to(jt.id); }}>
                              <span style={{ fontWeight:500 }}>{jt.first_name} {jt.last_name}</span>
                              {jt.email && <span style={{ color:'var(--text-secondary)' }}>· {jt.email}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Checklist */}
                    {lead.checklist && (() => {
                      const checkKeys = Object.keys(lead.checklist);
                      const done = checkKeys.filter(k => lead.checklist[k]).length;
                      return checkKeys.length > 0 ? (
                        <div style={{ padding:'0.5rem 1rem', borderTop:'1px solid var(--border)', fontSize:'0.75rem', color: done === checkKeys.length ? 'var(--success)' : 'var(--warning)' }}>
                          Move-in checklist: {done}/{checkKeys.length} {done === checkKeys.length ? '✅' : '⏳'}
                        </div>
                      ) : null;
                    })()}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        );
      })()}

      {/* ===== MANAGEMENT TAB ===== */}
      {activeTab === 'management' && (
        <div>
          <h3 style={{ margin:'0 0 1rem' }}>⚙️ Management & Jurisdiction</h3>
          <div className="card">
            <div className="form-grid">
              <div className="form-group">
                <label>Management Type</label>
                <select value={agencyForm.management_type} onChange={e => setAgencyForm(p => ({ ...p, management_type: e.target.value }))}>
                  <option value="self">Self Managed</option>
                  <option value="agency">Agency Managed</option>
                </select>
              </div>
              <div className="form-group">
                <label>Jurisdiction</label>
                <select value={agencyForm.jurisdiction} onChange={e => setAgencyForm(p => ({ ...p, jurisdiction: e.target.value }))}>
                  <option value="england">England</option>
                  <option value="scotland">Scotland</option>
                  <option value="wales">Wales</option>
                  <option value="northern_ireland">Northern Ireland</option>
                  <option value="ireland">Ireland 🇮🇪</option>
                </select>
              </div>
              {agencyForm.management_type === 'agency' && (<>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Managing Agency</label>
                  <ContractorPicker value={agencyForm.managing_contractor_id} onChange={(id) => setAgencyForm(p => ({ ...p, managing_contractor_id: id }))} placeholder="— Select the agency managing this property —" />
                  <small style={{ color:'var(--text-secondary)', fontSize:'0.75rem' }}>The agency's contact details are managed once in Contractors — the same agency can manage several properties without re-entering their details each time.</small>
                </div>
                <div className="form-group">
                  <label>Management Fee (%)</label>
                  <input type="number" step="0.1" value={agencyForm.agency_fee_percent} onChange={e => setAgencyForm(p => ({ ...p, agency_fee_percent: e.target.value }))} onWheel={e=>e.target.blur()} placeholder="e.g. 10" />
                  <small style={{ color:'var(--text-secondary)', fontSize:'0.75rem' }}>Specific to this property — the same agency may charge different rates on different properties</small>
                </div>
              </>)}
            </div>
            <div style={{ marginTop:'1rem' }}>
              <button className="btn btn-primary" onClick={handleSaveAgency}>💾 Save Management Settings</button>
            </div>
          </div>

          {/* Current settings summary */}
          <div className="card" style={{ marginTop:'1rem' }}>
            <div style={{ fontWeight:600, marginBottom:'0.75rem', fontSize:'0.875rem' }}>Current Settings</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem', fontSize:'0.875rem' }}>
              {[
                ['Management Type', property.management_type === 'agency' ? '🏢 Agency Managed' : '✅ Self Managed'],
                ['Jurisdiction', property.jurisdiction ? property.jurisdiction.replace('_',' ').replace(/\b\w/g, c => c.toUpperCase()) : 'England'],
                property.management_type === 'agency' && ['Managing Agency', property.managingContractorName || property.agency_name || '—'],
                property.management_type === 'agency' && (property.managingContractorMobile || property.managingContractorEmail || property.agency_contact) && ['Agency Contact', property.managingContractorMobile || property.managingContractorEmail || property.agency_contact || '—'],
                property.management_type === 'agency' && ['Management Fee', property.agency_fee_percent ? `${property.agency_fee_percent}%` : '—'],
              ].filter(Boolean).map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize:'0.7rem', color:'var(--text-secondary)', textTransform:'uppercase', marginBottom:'0.2rem' }}>{label}</div>
                  <div style={{ fontWeight:500 }}>{value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== AGENCY MANAGEMENT MODAL ===== */}
      {showAgencyEdit && (
        <div className="modal-overlay" onClick={() => setShowAgencyEdit(false)}>
          <div className="modal" style={{ maxWidth:'480px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">🏢 Management Settings</span>
              <button className="modal-close" onClick={() => setShowAgencyEdit(false)}>×</button>
            </div>
            <div className="form-group">
              <label>Management Type</label>
              <select value={agencyForm.management_type} onChange={e => setAgencyForm(p => ({ ...p, management_type: e.target.value }))}>
                <option value="self">Self Managed</option>
                <option value="agency">Agency Managed</option>
              </select>
            </div>
            <div className="form-group">
              <label>Jurisdiction</label>
              <select value={agencyForm.jurisdiction} onChange={e => setAgencyForm(p => ({ ...p, jurisdiction: e.target.value }))}>
                <option value="england">England</option>
                <option value="scotland">Scotland</option>
                <option value="wales">Wales</option>
                <option value="northern_ireland">Northern Ireland</option>
                <option value="ireland">Ireland 🇮🇪</option>
              </select>
            </div>
            {agencyForm.management_type === 'agency' && (
              <>
                <div className="form-group">
                  <label>Managing Agency</label>
                  <ContractorPicker value={agencyForm.managing_contractor_id} onChange={(id) => setAgencyForm(p => ({ ...p, managing_contractor_id: id }))} placeholder="— Select the agency managing this property —" />
                </div>
                <div className="form-group">
                  <label>Management Fee %</label>
                  <input type="number" step="0.1" value={agencyForm.agency_fee_percent} onChange={e => setAgencyForm(p => ({ ...p, agency_fee_percent: e.target.value }))} onWheel={e=>e.target.blur()} placeholder="e.g. 10" />
                  <small style={{ color:'var(--text-secondary)', fontSize:'0.75rem' }}>Specific to this property — log the fees themselves manually as expenses</small>
                </div>
              </>
            )}
            <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end', marginTop:'1rem' }}>
              <button className="btn btn-secondary" onClick={() => setShowAgencyEdit(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveAgency}>💾 Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SERVICE REQUESTS TAB ===== */}
      {activeTab === 'service-requests' && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
            <div>
              <h3 style={{ margin:0 }}>Service Requests</h3>
              <p style={{ margin:'0.25rem 0 0', fontSize:'0.875rem', color:'var(--text-secondary)' }}>
                {propServiceRequests.filter(r => !['completed','closed','cancelled'].includes(r.status)).length} active ·{' '}
                {propServiceRequests.filter(r => ['completed','closed'].includes(r.status)).length} completed
              </p>
            </div>
            <a href="/service-requests" style={{ textDecoration:'none' }}>
              <button className="btn btn-secondary" style={{ fontSize:'0.875rem' }}>View All Requests →</button>
            </a>
          </div>

          {srLoading ? (
            <div style={{ textAlign:'center', padding:'2rem', color:'var(--text-secondary)' }}>Loading...</div>
          ) : propServiceRequests.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <span>🔧</span>
                <h3>No service requests</h3>
                <p>Maintenance requests for this property will appear here</p>
                <a href="/service-requests"><button className="btn btn-primary">+ New Request</button></a>
              </div>
            </div>
          ) : (
            <div style={{ display:'grid', gap:'0.75rem' }}>
              {propServiceRequests
                .sort((a,b) => {
                  const priOrder = { emergency:0, high:1, medium:2, low:3 };
                  return (priOrder[a.priority]??2) - (priOrder[b.priority]??2);
                })
                .map(sr => {
                  const STATUS_COLOURS = {
                    new:            { bg:'#f1f5f9', text:'#475569', label:'New' },
                    assigned:       { bg:'#eff6ff', text:'#1e40af', label:'Assigned' },
                    quote_received: { bg:'#fef3c7', text:'#92400e', label:'Quote Received' },
                    in_progress:    { bg:'#ecfdf5', text:'#065f46', label:'In Progress' },
                    awaiting_parts: { bg:'#fff7ed', text:'#9a3412', label:'Awaiting Parts' },
                    completed:      { bg:'#f0fdf4', text:'#166534', label:'Completed' },
                    closed:         { bg:'#f8fafc', text:'#64748b', label:'Closed' },
                    cancelled:      { bg:'#fef2f2', text:'#991b1b', label:'Cancelled' },
                  };
                  const PRIORITY_COLOURS = {
                    low:       '#166534', medium:'#1e40af',
                    high:      '#9a3412', emergency:'#991b1b',
                  };
                  const stsCfg = STATUS_COLOURS[sr.status] || STATUS_COLOURS.new;
                  const priColor = PRIORITY_COLOURS[sr.priority] || '#1e40af';
                  const isActive = !['completed','closed','cancelled'].includes(sr.status);
                  return (
                    <div key={sr.id} className="card" style={{ borderLeft:`4px solid ${priColor}`, opacity: isActive ? 1 : 0.75 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:'0.95rem', marginBottom:'0.25rem' }}>{sr.title}</div>
                          <div style={{ fontSize:'0.8rem', color:'var(--text-secondary)', marginBottom:'0.5rem' }}>
                            {sr.category}
                            {sr.room_reference ? ` · Room: ${sr.room_reference}` : ''}
                            {sr.contractor_name ? ` · 🔧 ${sr.contractor_name}` : ''}
                          </div>
                          <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap', alignItems:'center' }}>
                            <span style={{ background:stsCfg.bg, color:stsCfg.text, fontSize:'0.7rem', fontWeight:700, padding:'0.2rem 0.5rem', borderRadius:'0.25rem' }}>
                              {stsCfg.label}
                            </span>
                            <span style={{ fontSize:'0.7rem', color:priColor, fontWeight:600, textTransform:'capitalize' }}>
                              {sr.priority === 'emergency' ? '🚨 Emergency' : sr.priority}
                            </span>
                          </div>
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0, marginLeft:'1rem', fontSize:'0.75rem', color:'var(--text-secondary)' }}>
                          <div>{sr.reported_date ? (() => { const [y,m,d] = sr.reported_date.substring(0,10).split('-'); return `${d}/${m}/${y}`; })() : '—'}</div>
                          {sr.quote_amount && <div style={{ fontWeight:700, color:'#166534', marginTop:'0.25rem' }}>{symbolFor(cur)}{parseFloat(sr.quote_amount).toFixed(2)}</div>}
                          {sr.actual_cost && <div style={{ fontWeight:700, marginTop:'0.25rem' }}>{symbolFor(cur)}{parseFloat(sr.actual_cost).toFixed(2)}</div>}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {/* ===== ROOMS TAB ===== */}
      {activeTab === 'rooms' && isHMO && (
        <div>
          {/* Single Let - show one tenant directly */}
          {(property.propertyType === 'single-let' || property.propertyType === 'Single Let') && rooms.length === 1 ? (
            <div>
              <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:'0.5rem', padding:'0.75rem 1rem', marginBottom:'1rem', fontSize:'0.875rem', color:'#15803d' }}>
                🏠 Single Let — one tenant for the whole property
              </div>
              {(() => {
                const room = rooms[0];
                const isOccupied = room.status === 'occupied';
                const paidThisMonth = propPayments.some(p => p.month === currentMonth && String(p.roomId||p.room_id) === String(room.id));
                return (
                  <div className="card" style={{ borderLeft:`4px solid ${isOccupied?'var(--success)':'var(--danger)'}` }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1rem' }}>
                      <div>
                        <h3 style={{ margin:0, fontSize:'1.25rem' }}>{room.tenantName || 'No Tenant'}</h3>
                        <span className={`badge ${isOccupied?'badge-success':'badge-danger'}`} style={{ marginTop:'0.5rem', display:'inline-block' }}>{isOccupied ? '✅ Occupied' : '❌ Vacant'}</span>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontWeight:700, fontSize:'1.25rem', color:'var(--success)' }}>{formatCurrency(room.rentAmount||0, property.currency)}/mo</div>
                        <div style={{ fontSize:'0.75rem', color:'var(--text-secondary)' }}>Due day {room.paymentDueDay||1}</div>
                      </div>
                    </div>
                    {room.leaseStart && <div style={{ marginBottom:'0.5rem', fontSize:'0.875rem' }}><span style={{ color:'var(--text-secondary)' }}>Lease: </span>{formatDate(room.leaseStart)} – {room.leaseEnd ? formatDate(room.leaseEnd) : 'Rolling'}</div>}
                    {isOccupied && !paidThisMonth && <div style={{ marginBottom:'1rem' }}><span className="badge badge-warning">⏳ Not Paid This Month</span></div>}
                    {isOccupied && paidThisMonth && <div style={{ marginBottom:'1rem' }}><span className="badge badge-success">✅ Paid This Month</span></div>}
                    <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap' }}>
                      {isOccupied && !paidThisMonth && <button className="btn btn-success" onClick={() => { setPaymentForm({ roomId:room.id, month:currentMonth, amount:String(room.rentAmount||''), paidDate:new Date().toISOString().split('T')[0] }); setError(''); setShowPaymentModal(true); }}>💰 Record Payment</button>}
                      <button className="btn btn-secondary" onClick={() => setEditingRoom({...room})}>✏️ Edit Tenant Details</button>
                      {isOccupied && <button className="btn btn-secondary" onClick={() => { setVacantRoom(room); setVacantForm({ date:new Date().toISOString().split('T')[0], notes:'' }); setShowVacantModal(true); }}>Mark Vacant</button>}
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div>
              <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'1rem' }}>
                <button className="btn btn-primary" onClick={() => { setNewRoomForm({ roomName:'', tenantName:'', rentAmount:'', paymentDueDay:1, status:'Vacant', leaseStart:'', leaseEnd:'' }); setShowAddRoomModal(true); }}>+ Add Room</button>
              </div>
              {rooms.length === 0 ? (
                <div className="card"><div className="empty-state"><span>🛏️</span><h3>No Rooms</h3><p>Add rooms to this property</p></div></div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:'1rem' }}>
                  {rooms.map(room => {
                    const isOccupied = room.status === 'occupied';
                    const paidThisMonth = propPayments.some(p => String(p.roomId||p.room_id)===String(room.id) && p.month===currentMonth);
                    return (
                      <div key={room.id} className="card" style={{ borderLeft:`4px solid ${isOccupied?'var(--success)':'var(--danger)'}` }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.75rem' }}>
                          <div>
                            <strong style={{ fontSize:'1rem' }}>{room.roomName}</strong>
                            <div style={{ marginTop:'0.25rem' }}>
                              <span className={`badge ${isOccupied?'badge-success':'badge-danger'}`} style={{ fontSize:'0.7rem' }}>
                                {isOccupied ? '✅ Occupied' : '❌ Vacant'}
                              </span>
                            </div>
                          </div>
                          <div style={{ textAlign:'right' }}>
                            <div style={{ fontWeight:700, color:'var(--success)' }}>{formatCurrency(room.rentAmount||0, property.currency)}/mo</div>
                            <div style={{ fontSize:'0.75rem', color:'var(--text-secondary)' }}>Due day {room.paymentDueDay||1}</div>
                          </div>
                        </div>
                        {isOccupied && (
                          <div style={{ marginBottom:'0.75rem', fontSize:'0.875rem' }}>
                            {(() => {
                              const linkedTenant = propTenants.find(t => String(t.room_id) === String(room.id) && t.status === 'active');
                              return linkedTenant ? (
                                <div><span style={{ color:'var(--text-secondary)' }}>Tenant: </span><strong>{linkedTenant.first_name} {linkedTenant.last_name}</strong>
                                  {linkedTenant.lease_start && <div><span style={{ color:'var(--text-secondary)' }}>Lease: </span>{formatDate(linkedTenant.lease_start)} – {linkedTenant.lease_end ? formatDate(linkedTenant.lease_end) : 'Rolling'}</div>}
                                </div>
                              ) : (
                                <div><span style={{ color:'var(--text-secondary)' }}>Tenant: </span><strong>{room.tenantName||'N/A'}</strong>
                                  {room.leaseStart && <div><span style={{ color:'var(--text-secondary)' }}>Lease: </span>{formatDate(room.leaseStart)} – {room.leaseEnd?formatDate(room.leaseEnd):'Rolling'}</div>}
                                </div>
                              );
                            })()}
                            <div style={{ marginTop:'0.375rem' }}>
                              <span className={`badge ${paidThisMonth?'badge-success':'badge-warning'}`} style={{ fontSize:'0.7rem' }}>
                                {paidThisMonth ? '✅ Paid This Month' : '⏳ Not Paid This Month'}
                              </span>
                            </div>
                          </div>
                        )}
                        {!isOccupied && room.vacantSince && (
                          <div style={{ marginBottom:'0.75rem', fontSize:'0.875rem', color:'var(--text-secondary)' }}>
                            Vacant since: {formatDate(room.vacantSince)}
                          </div>
                        )}
                        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                          {isOccupied && !paidThisMonth && (
                            <button className="btn btn-success" style={{ padding:'0.25rem 0.5rem', fontSize:'0.75rem' }}
                              onClick={() => { setPaymentForm({ roomId:room.id, month:currentMonth, amount:String(room.rentAmount||''), paidDate:new Date().toISOString().split('T')[0] }); setError(''); setShowPaymentModal(true); }}>
                              💰 Record Payment
                            </button>
                          )}
                          {isOccupied && (() => {
                            const linkedTenant = propTenants.find(t => String(t.room_id) === String(room.id) && t.status === 'active');
                            return linkedTenant ? (
                              <button className="btn btn-secondary" style={{ padding:'0.25rem 0.5rem', fontSize:'0.75rem' }}
                                onClick={() => { setActiveTab('tenants'); loadPropTenants(); openTenantView(linkedTenant); }}>
                                👤 View Tenant
                              </button>
                            ) : null;
                          })()}
                          {isOccupied && (
                            <button className="btn btn-secondary" style={{ padding:'0.25rem 0.5rem', fontSize:'0.75rem' }}
                              onClick={() => { setVacantRoom(room); setVacantForm({ date:new Date().toISOString().split('T')[0], notes:'' }); setShowVacantModal(true); }}>
                              Mark Vacant
                            </button>
                          )}
                          <button className="btn btn-secondary" style={{ padding:'0.25rem 0.5rem', fontSize:'0.75rem' }} onClick={() => setEditingRoom({...room})}>✏️ Edit</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== PAYMENTS TAB ===== */}
      {activeTab === 'payments' && (() => {
        const payByYear = {};
        propPayments.forEach(p => { const yr = p.month ? p.month.substring(0,4) : 'Unknown'; if (!payByYear[yr]) payByYear[yr]=[]; payByYear[yr].push(p); });
        const yearEntries = Object.entries(payByYear).sort(([a],[b]) => b.localeCompare(a));
        return (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
              <span style={{ fontWeight:600 }}>Payment History ({propPayments.length})</span>
              <button className="btn btn-primary" onClick={() => { setEditingPayment(null); setPaymentForm({ roomId:'', month:currentMonth, amount:'', paidDate:new Date().toISOString().split('T')[0], tenantId:'' }); setError(''); if (!propTenants.length) loadPropTenants(); setShowPaymentModal(true); }}>+ Record Payment</button>
            </div>
            {propPayments.length === 0 ? (
              <div className="card"><div className="empty-state"><span>💰</span><h3>No Payments</h3><p>Record rent payments for this property</p></div></div>
            ) : yearEntries.map(([year, payments]) => {
              const yearTotal = payments.reduce((s,p)=>s+parseFloat(p.amount||0),0);
              const isCollapsed = collapsedPayYears[year];
              return (
                <div key={year} className="card" style={{ marginBottom:'1rem' }}>
                  <div onClick={() => togglePayYear(year)} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer', userSelect:'none' }}>
                    <span style={{ fontWeight:700, color:'var(--brand-green)', fontSize:'1rem' }}>{isCollapsed?'▶':'▼'} {year} <span style={{ fontWeight:400, color:'var(--text-secondary)', fontSize:'0.875rem' }}>({payments.length} payment{payments.length!==1?'s':''})</span></span>
                    <span style={{ fontWeight:700, color:'var(--success)' }}>{fmtCur(yearTotal)}</span>
                  </div>
                  {!isCollapsed && (() => {
                    // Group by month within the year
                    const byMonth = {};
                    [...payments].sort((a,b)=>(b.month||'').localeCompare(a.month||'')).forEach(p => {
                      const mo = p.month || 'Unknown';
                      if (!byMonth[mo]) byMonth[mo] = [];
                      byMonth[mo].push(p);
                    });
                    return (
                      <div style={{ marginTop:'1rem' }}>
                        {Object.entries(byMonth).map(([month, monthPays]) => {
                          const monthTotal = monthPays.reduce((s,p)=>s+parseFloat(p.amount||0),0);
                          return (
                            <div key={month} style={{ marginBottom:'0.75rem' }}>
                              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.4rem 0.75rem', background:'var(--bg-light)', borderRadius:'0.375rem', marginBottom:'0.25rem', fontSize:'0.8125rem', fontWeight:600 }}>
                                <span style={{ color:'var(--brand-green)' }}>{formatMonth(month)}</span>
                                <span style={{ color:'var(--success)' }}>{fmtCur(monthTotal)}</span>
                              </div>
                              <div className="table-container"><table className="table">
                                <thead><tr><th>Room / Tenant</th><th>Amount</th><th>Paid Date</th><th>Actions</th></tr></thead>
                                <tbody>{monthPays.map(p => {
                                  const room = rooms.find(r => String(r.id)===String(p.roomId||p.room_id));
                                  // BUG FIX: This was always showing the room's CURRENT
                                  // occupant, ignoring the payment's own tenant_id link —
                                  // meaning a correctly-backdated payment still displayed
                                  // against today's tenant instead of who it was actually
                                  // paid by. Now prefers the linked tenant, falling back to
                                  // a date-bounded match against propTenants' own occupancy
                                  // periods — not the room's CURRENT occupant — for the
                                  // subset of payments with no tenant_id at all (older
                                  // payments recorded before this existed). Same fallback
                                  // already built server-side and in Transactions.js.
                                  const linkedTenant = (p.tenantId || p.tenant_id) ? propTenants.find(t => String(t.id) === String(p.tenantId || p.tenant_id)) : null;
                                  const paidDateStr = (p.paidDate || p.paid_date || '').substring(0,10);
                                  // BUG FIX: several moved-out tenants have no
                                  // lease_start recorded at all (only a
                                  // move_out_date) — requiring lease_start
                                  // meant they could never match any
                                  // historical payment. Try a precise match
                                  // (lease_start defined and covers the date)
                                  // first; only fall back to "no lease_start,
                                  // but paid on/before their move-out, closest
                                  // candidate" when nothing precise matches.
                                  // BUG FIX: for a non-HMO property, room is
                                  // always undefined (rooms is empty), which
                                  // made String(room?.id) evaluate to the
                                  // literal string "undefined" — and since a
                                  // non-HMO tenant's own room_id is ALSO
                                  // undefined, both sides happened to match
                                  // by coincidence. propTenants is already
                                  // scoped to this property (see
                                  // loadPropTenants), so this "worked" only
                                  // because every candidate matched the same
                                  // accidental string — fragile, and would
                                  // silently break if either side's data
                                  // shape ever changed. Now explicit: match
                                  // by room for HMO, match everyone in
                                  // propTenants (already property-scoped)
                                  // for non-HMO.
                                  const roomCandidates = paidDateStr ? propTenants.filter(t => isHMO ? String(t.room_id||t.roomId) === String(room?.id) : true) : [];
                                  const precise = roomCandidates.find(t => {
                                    if (!t.lease_start || t.lease_start.substring(0,10) > paidDateStr) return false;
                                    const candidateEnds = [t.lease_end, t.move_out_date].filter(Boolean).map(d=>d.substring(0,10));
                                    if (candidateEnds.length && candidateEnds.sort()[0] < paidDateStr) return false;
                                    return true;
                                  });
                                  // Same fix as Transactions.js — only checked
                                  // move_out_date, missing tenants who have
                                  // lease_end but no move_out_date recorded.
                                  const undatedCandidates = roomCandidates
                                    .filter(t => !t.lease_start && (t.move_out_date || t.lease_end) && (t.move_out_date || t.lease_end).substring(0,10) >= paidDateStr)
                                    .sort((a,b) => (a.move_out_date||a.lease_end).localeCompare(b.move_out_date||b.lease_end));
                                  const byDateTenant = !linkedTenant ? (precise || undatedCandidates[0] || null) : null;
                                  const resolvedTenant = linkedTenant || byDateTenant;
                                  const tenantLabel = resolvedTenant
                                    ? `${resolvedTenant.first_name} ${resolvedTenant.last_name||''}`.trim() + (resolvedTenant.status==='moved_out' ? ' (moved out)' : '')
                                    : (room?.tenantName || '');
                                  return (<tr key={p.id}>
                                    <td>{room?.roomName || ''}{tenantLabel ? ` – ${tenantLabel}` : ''}</td>
                                    <td style={{fontWeight:600,color:'var(--success)'}}>{fmtCur(p.amount)}</td>
                                    <td>{formatDate(p.paidDate||p.paid_date)}</td>
                                    <td>
                                      <div style={{ display:'flex', gap:'0.4rem' }}>
                                        <button className="btn btn-secondary" style={{padding:'0.25rem 0.5rem',fontSize:'0.75rem'}} onClick={()=>openEditPayment(p)}>✏️ Edit</button>
                                        <button className="btn btn-danger" style={{padding:'0.25rem 0.5rem',fontSize:'0.75rem'}} onClick={()=>handleDeletePayment(p.id)}>Delete</button>
                                      </div>
                                    </td>
                                  </tr>);
                                })}</tbody>
                              </table></div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ===== EXPENSES TAB ===== */}
      {activeTab === 'expenses' && (() => {
        const expByYear = {};
        propExpenses.forEach(e => { const yr = e.date ? e.date.substring(0,4) : 'Unknown'; if (!expByYear[yr]) expByYear[yr]=[]; expByYear[yr].push(e); });
        const yearEntries = Object.entries(expByYear).sort(([a],[b]) => b.localeCompare(a));
        return (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
              <span style={{ fontWeight:600 }}>Property Expenses ({propExpenses.length})</span>
              <button className="btn btn-primary" onClick={() => { setExpenseForm({ category:'', amount:'', date:new Date().toISOString().split('T')[0], description:'', paid:false }); setError(''); setShowExpenseModal(true); }}>+ Add Expense</button>
            </div>
            {propExpenses.length === 0 ? (
              <div className="card"><div className="empty-state"><span>📤</span><h3>No Expenses</h3><p>Track maintenance and property costs here</p></div></div>
            ) : yearEntries.map(([year, expenses]) => {
              const yearTotal = expenses.reduce((s,e)=>s+parseFloat(e.amount||0),0);
              const yearPaid = expenses.filter(e=>e.paid===true||e.paid==='true').reduce((s,e)=>s+parseFloat(e.amount||0),0);
              const isCollapsed = collapsedExpYears[year];
              return (
                <div key={year} className="card" style={{ marginBottom:'1rem' }}>
                  <div onClick={() => toggleExpYear(year)} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer', userSelect:'none' }}>
                    <span style={{ fontWeight:700, color:'var(--brand-green)', fontSize:'1rem' }}>{isCollapsed?'▶':'▼'} {year} <span style={{ fontWeight:400, color:'var(--text-secondary)', fontSize:'0.875rem' }}>({expenses.length} expense{expenses.length!==1?'s':''})</span></span>
                    <div style={{ display:'flex', gap:'1.5rem', fontSize:'0.875rem' }}>
                      <span style={{ color:'var(--success)', fontWeight:600 }}>Paid: {fmtCur(yearPaid)}</span>
                      <span style={{ fontWeight:700 }}>Total: {fmtCur(yearTotal)}</span>
                    </div>
                  </div>
                  {!isCollapsed && (() => {
                    // Group by month within the year
                    const byMonth = {};
                    [...expenses].sort((a,b)=>new Date(b.date)-new Date(a.date)).forEach(exp => {
                      const mo = exp.date ? exp.date.substring(0,7) : 'Unknown';
                      if (!byMonth[mo]) byMonth[mo] = [];
                      byMonth[mo].push(exp);
                    });
                    return (
                      <div style={{ marginTop:'1rem' }}>
                        {Object.entries(byMonth).map(([month, monthExps]) => {
                          const monthTotal = monthExps.reduce((s,e)=>s+parseFloat(e.amount||0),0);
                          const monthPaid = monthExps.filter(e=>e.paid===true||e.paid==='true').reduce((s,e)=>s+parseFloat(e.amount||0),0);
                          return (
                            <div key={month} style={{ marginBottom:'0.75rem' }}>
                              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.4rem 0.75rem', background:'var(--bg-light)', borderRadius:'0.375rem', marginBottom:'0.25rem', fontSize:'0.8125rem', fontWeight:600 }}>
                                <span style={{ color:'var(--brand-green)' }}>{new Date(month+'-15').toLocaleDateString('en-GB',{month:'long',year:'numeric'})}</span>
                                <span style={{ display:'flex', gap:'1rem' }}>
                                  <span style={{color:'var(--success)'}}>Paid: {fmtCur(monthPaid)}</span>
                                  <span>Total: {fmtCur(monthTotal)}</span>
                                </span>
                              </div>
                              <div className="table-container"><table className="table">
                                <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Amount</th><th>Status</th><th>Actions</th></tr></thead>
                                <tbody>{monthExps.map(exp => (
                                  <tr key={exp.id}>
                                    <td>{formatDate(exp.date)}</td>
                                    <td>{exp.category}</td>
                                    <td style={{color:'var(--text-secondary)'}}>{exp.description||'–'}</td>
                                    <td style={{fontWeight:600,color:'var(--danger)'}}>{fmtCur(exp.amount)}</td>
                                    <td><button className={`badge ${(exp.paid===true||exp.paid==='true')?'badge-success':'badge-warning'}`} onClick={()=>handleToggleExpensePaid(exp)} style={{cursor:'pointer',border:'none'}}>{(exp.paid===true||exp.paid==='true')?'✅ Paid':'⏳ Unpaid'}</button></td>
                                    <td><button className="btn btn-danger" style={{padding:'0.25rem 0.5rem',fontSize:'0.75rem'}} onClick={()=>handleDeleteExpense(exp.id)}>Delete</button></td>
                                  </tr>
                                ))}</tbody>
                              </table></div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ===== CAPITAL TAB ===== */}
      {activeTab === 'capital' && (
        <div>
          {capitalData.rateExpiryDate && (
            <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'0.5rem',padding:'1rem',marginBottom:'1.5rem',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:'0.75rem'}}>
              <div>
                <strong style={{color:'#1e40af'}}>🏦 Mortgage Rate Expires: {new Date(capitalData.rateExpiryDate).toLocaleDateString('en-GB')}</strong>
                <p style={{margin:'0.25rem 0 0',fontSize:'0.875rem',color:'#1e40af'}}>Create calendar reminders to review mortgage options before your rate expires.</p>
              </div>
              <button className="btn btn-primary" style={{flexShrink:0,fontSize:'0.875rem'}} onClick={async()=>{
                try{
                  const expiryDate=new Date(capitalData.rateExpiryDate);
                  const sixMonths=new Date(expiryDate); sixMonths.setMonth(sixMonths.getMonth()-6);
                  const ninety=new Date(expiryDate); ninety.setDate(ninety.getDate()-90);
                  const provider=capitalData.mortgageProvider||'N/A';
                  const addr=property.address;
                  const tasks=[
                    {id:`mort-6m-${property.id}`,date:sixMonths.toISOString().split('T')[0],title:`🏦 Mortgage Review - ${addr}`,description:`Rate expires ${expiryDate.toLocaleDateString('en-GB')}. 6 months before — start comparing rates. Provider: ${provider}.`,type:'mortgage-reminder',color:'#3b82f6',completed:false,propertyId:property.id},
                    {id:`mort-90d-${property.id}`,date:ninety.toISOString().split('T')[0],title:`⚠️ Mortgage Rate Expires Soon - ${addr}`,description:`Rate expires in 90 days (${expiryDate.toLocaleDateString('en-GB')}). Review urgently. Provider: ${provider}.`,type:'mortgage-reminder',color:'#f59e0b',completed:false,propertyId:property.id},
                    {id:`mort-exp-${property.id}`,date:expiryDate.toISOString().split('T')[0],title:`🔴 Mortgage Rate Expires Today - ${addr}`,description:`Rate expires today. Provider: ${provider}. Ensure you have switched or extended.`,type:'mortgage-reminder',color:'#dc2626',completed:false,propertyId:property.id}
                  ];
                  let created=0;
                  for(const task of tasks){
                    const r=await calendarAPI.create(task);
                    setCalendarTasks(prev=>[r,...prev.filter(t=>t.id!==task.id)]);
                    created++;
                  }
                  alert(`✅ Created ${created} mortgage reminders in Calendar:
• 6 months before expiry
• 90 days before expiry
• On expiry date`);
                }catch(e){alert('Failed to create reminders: '+(e.error||e.message));}
              }}>📅 Create Mortgage Reminders</button>
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1.5rem' }}>
          <div className="card">
            <div className="card-header"><span className="card-title">💰 Capital Performance</span></div>
            {[
              ['Purchase Price', formatCurrency(capitalData.purchasePrice||0, property.currency)],
              ['Current Value', formatCurrency(capitalData.currentValue||0, property.currency)],
              ['Capital Growth', formatCurrency((parseFloat(capitalData.currentValue||0)-parseFloat(capitalData.purchasePrice||0)), property.currency)],
              ['Equity', formatCurrency(equity, property.currency)],
              ['Gross Yield', `${grossYield}%`],
              ['Annual Rental Income', formatCurrency(annualRent, property.currency)],
              ['Purchase Date', formatDate(capitalData.purchaseDate)],
            ].map(([label,value]) => (
              <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'0.625rem', borderBottom:'1px solid var(--border-light)' }}>
                <span style={{ color:'var(--text-secondary)', fontSize:'0.875rem' }}>{label}</span>
                <strong style={{ fontSize:'0.875rem' }}>{value}</strong>
              </div>
            ))}
          </div>
          <div className="card">
            <div className="card-header"><span className="card-title">🏦 Mortgage Details</span></div>
            {[
              ['Provider', capitalData.mortgageProvider||'–'],
              ['Balance', fmtCur(capitalData.mortgageBalance||0)],
              ['Interest Rate', `${capitalData.mortgageRate||0}%`],
              ['Repayment Type', capitalData.repaymentType||'–'],
              ['Mortgage Start Date', capitalData.mortgageStartDate ? formatDate(capitalData.mortgageStartDate) : '– (using purchase date)'],
              ['Total Mortgage Term', mortTermYears > 0 ? `${mortTermYears} years` : '–'],
              ['Est. Term Remaining', termRemainingYears !== null ? `~${termRemainingYears} years (from ${usingMortgageStartDate ? 'mortgage start' : 'purchase date'})` : '–'],
              ['Monthly Payment (est.)', fmtCur(monthlyMortgage)],
              ['Rate Expiry', formatDate(capitalData.rateExpiryDate)],
            ].map(([label,value]) => (
              <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'0.625rem', borderBottom:'1px solid var(--border-light)' }}>
                <span style={{ color:'var(--text-secondary)', fontSize:'0.875rem' }}>{label}</span>
                <strong style={{ fontSize:'0.875rem' }}>{value}</strong>
              </div>
            ))}
                  </div>
          </div>
        </div>
      )}

      {/* ===== LICENCES TAB ===== */}
      {activeTab === 'licences' && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
            <span style={{ fontWeight:600 }}>Documents ({licences.filter(l=>!l.archived).length})</span>
            <div style={{display:'flex',gap:'0.5rem'}}>
              {licences.filter(l=>!l.archived&&l.validTo).length>0&&(
                <button className="btn btn-secondary" style={{fontSize:'0.8rem'}} onClick={async()=>{
                  let created=0;
                  for(const lic of licences.filter(l=>!l.archived&&l.validTo)){
                    try{
                      const expiryDate=new Date(lic.validTo);
                      const reminderDate=new Date(expiryDate);
                      reminderDate.setDate(reminderDate.getDate()-30);
                      const calTask={id:`licence-${lic.id}`,date:reminderDate.toISOString().split('T')[0],title:`${lic.type} Expiring Soon - ${property.address}`,description:`Expires ${expiryDate.toLocaleDateString('en-GB')}. Ref: ${lic.referenceNumber||'N/A'}`,type:'licence-expiry',color:'#f59e0b',completed:false,propertyId:property.id,licenceId:lic.id};
                      const r=await calendarAPI.create(calTask);
                      setCalendarTasks(prev=>[r,...prev.filter(t=>t.id!==r.id&&t.id!==`licence-${lic.id}`)]);
                      created++;
                    }catch(e){console.warn('Reminder failed for',lic.type,e);}
                  }
                  alert(created>0?`✅ Created/updated ${created} calendar reminder${created!==1?'s':''} for licence expiry!`:'All licence reminders already up to date.');
                }}>📅 Sync Reminders</button>
              )}
              <button className="btn btn-secondary" onClick={() => { setNewSetName(''); setError(''); setShowSetModal(true); }}>🖼️ Add Photo Set</button>
              <div style={{ display:'flex', gap:'0.75rem' }}>
                <button className="btn btn-secondary" onClick={openBatchScan}>📷 Scan Document(s)</button>
                <button className="btn btn-primary" onClick={() => { setLicenceForm(defaultLicenceForm); setEditLicence(null); setError(''); setShowLicenceModal(true); }}>+ Add Document</button>
              </div>
            </div>
          </div>
          <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:'0.5rem', padding:'0.75rem 1rem', marginBottom:'1rem', fontSize:'0.875rem', color:'#15803d' }}>
            📁 Store compliance certificates, contracts, floor plans and property photos. Compliance certificates track expiry dates and create calendar reminders. Upload a new version when a certificate is renewed — old versions are kept in history.
          </div>
          {storageInfo && (
            <div style={{ marginBottom:'1rem' }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.75rem', color:'var(--text-secondary)', marginBottom:'0.25rem' }}>
                <span>Document storage ({storageInfo.plan} plan)</span>
                <span>{fmtBytes(storageInfo.used)} of {fmtBytes(storageInfo.limit)} used</span>
              </div>
              <div style={{ height:'6px', background:'#e5e7eb', borderRadius:'3px', overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${Math.min(100, storageInfo.percent)}%`, background: storageInfo.percent >= 90 ? 'var(--danger)' : storageInfo.percent >= 70 ? 'var(--warning)' : 'var(--success)' }} />
              </div>
              {storageInfo.atLimit && <div style={{ fontSize:'0.75rem', color:'var(--danger)', marginTop:'0.35rem' }}>Storage full — upgrade your plan to upload more documents.</div>}
            </div>
          )}

          {/* ── Photo Sets (galleries) ── */}
          {photoSets.length > 0 && (
            <div style={{ marginBottom:'1.5rem' }}>
              {photoSets.map(set => (
                <div key={set.group} className="card" style={{ marginBottom:'1rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem' }}>
                    <strong>🖼️ {set.name} <span style={{ fontWeight:400, color:'var(--text-secondary)', fontSize:'0.85rem' }}>({set.images.length} photo{set.images.length!==1?'s':''})</span></strong>
                    <div style={{ display:'flex', gap:'0.5rem' }}>
                      <label className="btn btn-secondary" style={{ padding:'0.25rem 0.6rem', fontSize:'0.75rem', cursor:'pointer', margin:0 }}>
                        {uploadingSet === set.group ? 'Uploading…' : '+ Add Photos'}
                        <input type="file" accept=".jpg,.jpeg,.png" multiple style={{ display:'none' }} disabled={uploadingSet === set.group}
                          onChange={e => { addToPhotoSet(set.group, set.name, Array.from(e.target.files)); e.target.value=''; }} />
                      </label>
                      <button className="btn btn-secondary" style={{ padding:'0.25rem 0.6rem', fontSize:'0.75rem', color:'var(--danger)' }} onClick={() => deletePhotoSet(set.group, set.images.length)}>Delete Set</button>
                    </div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(120px, 1fr))', gap:'0.6rem' }}>
                    {set.images.map(img => <GalleryTile key={img.id} doc={img} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
          {licences.filter(l=>!l.archived).length === 0 && photoSets.length === 0 ? (
            <div className="card"><div className="empty-state"><span>📁</span><h3>No Documents Yet</h3><p>Add compliance certificates (gas safety, EICR, EPC, HMO licence), contracts, floor plans or property photos</p><div style={{ display:'flex', gap:'0.75rem', justifyContent:'center' }}><button className="btn btn-secondary" onClick={openBatchScan}>📷 Scan Document(s)</button><button className="btn btn-primary" onClick={() => { setLicenceForm(defaultLicenceForm); setEditLicence(null); setShowLicenceModal(true); }}>Add First Document</button></div></div></div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:'1rem' }}>
              {licences.filter(l=>!l.archived).map(lic => {
                const isCompliance = isComplianceType(lic.type);
                const expired = isCompliance && lic.validTo && new Date(lic.validTo) < new Date();
                const expiringSoon = isCompliance && !expired && lic.validTo && (new Date(lic.validTo)-new Date()) < 30*24*60*60*1000;
                const borderColor = !isCompliance ? 'var(--border)' : expired ? 'var(--danger)' : expiringSoon ? 'var(--warning)' : 'var(--success)';
                return (
                  <div key={lic.id} className="card" style={{ borderLeft:`4px solid ${borderColor}` }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.5rem' }}>
                      <div>
                        <strong>{lic.type === 'Photos' ? '🖼️' : '📄'} {lic.type}</strong>
                        {isCompliance && (
                          <div style={{ marginTop:'0.25rem' }}>
                            <span className={`badge ${expired?'badge-danger':expiringSoon?'badge-warning':'badge-success'}`} style={{ fontSize:'0.7rem' }}>
                              {expired ? '❌ Expired' : expiringSoon ? '⚠️ Expiring Soon' : '✅ Valid'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {lic.description && <div style={{ fontSize:'0.875rem', color:'var(--text-secondary)', marginBottom:'0.375rem' }}>{lic.description}</div>}
                    {lic.referenceNumber && <div style={{ fontSize:'0.75rem', color:'var(--text-secondary)' }}>Ref: {lic.referenceNumber}</div>}
                    <div style={{ fontSize:'0.875rem', marginTop:'0.5rem' }}>
                      {lic.validFrom && <div><span style={{ color:'var(--text-secondary)' }}>Valid From: </span>{formatDate(lic.validFrom)}</div>}
                      {lic.validTo && <div><span style={{ color:'var(--text-secondary)' }}>Valid To: </span><strong style={{ color:expired?'var(--danger)':expiringSoon?'var(--warning)':'inherit' }}>{formatDate(lic.validTo)}</strong></div>}
                    </div>
                    {lic.notes && <div style={{ fontSize:'0.75rem', color:'var(--text-secondary)', marginTop:'0.5rem', fontStyle:'italic' }}>{lic.notes}</div>}

                    {/* ── Attached document (versioned, with thumbnail) ── */}
                    {(() => {
                      const current = currentDocForLicence(lic.id);
                      const history = docsForLicence(lic.id);
                      const groupKey = `licence-${lic.id}`;
                      const isUploading = uploadingFor === lic.id;
                      return (
                        <div style={{ marginTop:'0.75rem', paddingTop:'0.75rem', borderTop:'1px dashed var(--border)' }}>
                          {current ? (
                            <div>
                              <div style={{ display:'flex', alignItems:'flex-start', gap:'0.75rem' }}>
                                <DocThumb doc={current} />
                                <div style={{ fontSize:'0.8rem', minWidth:0, flex:1 }}>
                                  <div title={current.filename} style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight:500 }}>{current.filename}</div>
                                  <div style={{ color:'var(--text-secondary)', fontSize:'0.72rem', marginBottom:'0.4rem' }}>v{current.version_number} · {fmtBytes(current.file_size)}</div>
                                  <button className="btn btn-secondary" style={{ padding:'0.2rem 0.45rem', fontSize:'0.7rem' }} onClick={() => downloadDoc(current.id)}>⬇️ Download</button>
                                </div>
                              </div>
                              {history.length > 1 && (
                                <button
                                  onClick={() => setExpandedHistory(p => ({ ...p, [groupKey]: !p[groupKey] }))}
                                  style={{ background:'none', border:'none', color:'var(--primary)', cursor:'pointer', fontSize:'0.72rem', padding:'0.25rem 0', marginTop:'0.15rem' }}>
                                  {expandedHistory[groupKey] ? '▲ Hide' : `▼ ${history.length - 1} previous version${history.length - 1 !== 1 ? 's' : ''}`}
                                </button>
                              )}
                              {expandedHistory[groupKey] && history.filter(d => !d.is_current).map(d => (
                                <div key={d.id} style={{ display:'flex', alignItems:'center', gap:'0.5rem', fontSize:'0.72rem', color:'var(--text-secondary)', padding:'0.25rem 0 0.25rem 0.5rem' }}>
                                  <DocThumb doc={d} size={30} />
                                  <span style={{ flex:1 }}>v{d.version_number} · {fmtBytes(d.file_size)} · {formatDate(d.uploaded_at)}</span>
                                  <button className="btn btn-secondary" style={{ padding:'0.1rem 0.35rem', fontSize:'0.65rem' }} onClick={() => downloadDoc(d.id)}>⬇️</button>
                                  <button className="btn btn-secondary" style={{ padding:'0.1rem 0.35rem', fontSize:'0.65rem', color:'var(--danger)' }} title="Delete this version" onClick={() => deleteSingleDoc(d.id, false, history.length)}>🗑</button>
                                </div>
                              ))}
                              <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.4rem', flexWrap:'wrap' }}>
                                <label className="btn btn-secondary" style={{ padding:'0.2rem 0.45rem', fontSize:'0.7rem', cursor:'pointer', margin:0 }}>
                                  {isUploading ? 'Uploading…' : '⬆️ New Version'}
                                  <input type="file" accept={ACCEPTED_FILES} style={{ display:'none' }} disabled={isUploading}
                                    onChange={e => { uploadDocForLicence(lic, e.target.files[0]); e.target.value=''; }} />
                                </label>
                                <button className="btn btn-secondary" style={{ padding:'0.2rem 0.45rem', fontSize:'0.7rem', color:'var(--danger)' }} onClick={() => deleteSingleDoc(current.id, true, history.length)}>🗑 Delete current</button>
                                {history.length > 1 && (
                                  <button className="btn btn-secondary" style={{ padding:'0.2rem 0.45rem', fontSize:'0.7rem', color:'var(--danger)' }} onClick={() => deleteAllVersions(current.id, history.length)}>Delete all versions</button>
                                )}
                              </div>
                              <div style={{ fontSize:'0.68rem', color:'var(--text-secondary)', marginTop:'0.3rem' }}>{UPLOAD_HELP}</div>
                            </div>
                          ) : (
                            <div>
                              <label className="btn btn-secondary" style={{ padding:'0.25rem 0.55rem', fontSize:'0.72rem', cursor:'pointer', margin:0, display:'inline-block' }}>
                                {isUploading ? 'Uploading…' : '📎 Attach Document'}
                                <input type="file" accept={ACCEPTED_FILES} style={{ display:'none' }} disabled={isUploading}
                                  onChange={e => { uploadDocForLicence(lic, e.target.files[0]); e.target.value=''; }} />
                              </label>
                              <div style={{ fontSize:'0.68rem', color:'var(--text-secondary)', marginTop:'0.3rem' }}>{UPLOAD_HELP}</div>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.75rem' }}>
                      <button className="btn btn-secondary" style={{ padding:'0.25rem 0.5rem', fontSize:'0.75rem' }} onClick={() => openEditLicence(lic)}>✏️ Edit</button>
                      <button className="btn btn-danger" style={{ padding:'0.25rem 0.5rem', fontSize:'0.75rem' }} onClick={() => handleDeleteLicence(lic.id)}>Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== MODALS ===== */}

      {/* Add Room Modal */}
      {showAddRoomModal && (
        <div className="modal-overlay" onClick={() => setShowAddRoomModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">🛏️ Add Room</span><button className="modal-close" onClick={() => setShowAddRoomModal(false)}>×</button></div>
            {error && <div className="alert alert-danger">{error}</div>}
            <form onSubmit={handleAddRoom}>
              <div className="form-grid">
                <div className="form-group"><label>Room Name *</label><input value={newRoomForm.roomName} onChange={e=>setNewRoomForm(p=>({...p,roomName:e.target.value}))} required placeholder="e.g. Room 1, Master Bedroom" /></div>
                <div className="form-group"><label>Status</label>
                  <select value={newRoomForm.status} onChange={e=>setNewRoomForm(p=>({...p,status:e.target.value}))}>
                    <option>Occupied</option><option>Vacant</option>
                  </select>
                </div>
                <div className="form-group"><label>Monthly Rent ({symbolFor(property.currency)})</label><input type="number" step="0.01" value={newRoomForm.rentAmount} onChange={e=>setNewRoomForm(p=>({...p,rentAmount:e.target.value}))} onWheel={e=>e.target.blur()} placeholder="0.00" /></div>
                <div className="form-group"><label>Payment Due Day</label>
                  <select value={newRoomForm.paymentDueDay} onChange={e=>setNewRoomForm(p=>({...p,paymentDueDay:e.target.value}))}>
                    {Array.from({length:28},(_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}
                  </select>
                </div>
              </div>
              <small style={{ color:'var(--text-secondary)', fontSize:'0.75rem', display:'block', marginBottom:'1rem' }}>
                💡 After adding the room, use the <strong>Tenants tab</strong> to add full tenant details
              </small>
              <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddRoomModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading?'Saving...':'✅ Add Room'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Room Modal */}
      {editingRoom && (
        <div className="modal-overlay" onClick={() => setEditingRoom(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">✏️ Edit Room: {editingRoom.roomName}</span><button className="modal-close" onClick={() => setEditingRoom(null)}>×</button></div>
            {error && <div className="alert alert-danger">{error}</div>}
            <div className="form-grid">
              <div className="form-group"><label>Room Name *</label><input value={editingRoom.roomName} onChange={e=>setEditingRoom(p=>({...p,roomName:e.target.value}))} required /></div>
              <div className="form-group"><label>Status</label>
                <select value={editingRoom.status==='occupied'?'Occupied':'Vacant'} onChange={e=>setEditingRoom(p=>({...p,status:e.target.value==='Occupied'?'occupied':'vacant'}))}>
                  <option>Occupied</option><option>Vacant</option>
                </select>
              </div>
              <div className="form-group"><label>Monthly Rent ({symbolFor(property.currency)})</label><input type="number" step="0.01" value={editingRoom.rentAmount||''} onChange={e=>setEditingRoom(p=>({...p,rentAmount:e.target.value}))} onWheel={e=>e.target.blur()} /></div>
              <div className="form-group"><label>Payment Due Day</label>
                <select value={editingRoom.paymentDueDay||1} onChange={e=>setEditingRoom(p=>({...p,paymentDueDay:parseInt(e.target.value)}))}>
                  {Array.from({length:28},(_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}
                </select>
              </div>
            </div>
            <small style={{ color:'var(--text-secondary)', fontSize:'0.75rem', display:'block', marginBottom:'1rem' }}>
              💡 To update tenant details, lease dates or contact info use the <strong>Tenants tab</strong>
            </small>
            <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end', marginTop:'1rem' }}>
              <button className="btn btn-secondary" onClick={() => setEditingRoom(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={loading} onClick={() => handleUpdateRoom(editingRoom)}>{loading?'Saving...':'💾 Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Vacant Modal */}
      {showVacantModal && vacantRoom && (
        <div className="modal-overlay" onClick={() => setShowVacantModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">Mark Room as Vacant</span><button className="modal-close" onClick={() => setShowVacantModal(false)}>×</button></div>
            <p style={{ marginBottom:'1rem' }}>Marking <strong>{vacantRoom.roomName}</strong> ({vacantRoom.tenantName}) as vacant.</p>
            <div className="form-group"><label>Vacant From *</label><input type="date" value={vacantForm.date} onChange={e=>setVacantForm(p=>({...p,date:e.target.value}))} required /></div>
            <div className="form-group"><label>Notes</label><textarea value={vacantForm.notes} onChange={e=>setVacantForm(p=>({...p,notes:e.target.value}))} rows="3" placeholder="Reason for vacancy, condition notes..." /></div>
            <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowVacantModal(false)}>Cancel</button>
              <button className="btn btn-danger" disabled={loading} onClick={handleMarkVacant}>{loading?'Saving...':'✅ Mark as Vacant'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => { setShowPaymentModal(false); setEditingPayment(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">{editingPayment ? '✏️ Edit Payment' : '💰 Record Payment'}</span><button className="modal-close" onClick={() => { setShowPaymentModal(false); setEditingPayment(null); }}>×</button></div>
            {error && <div className="alert alert-danger">{error}</div>}
            <form onSubmit={handleAddPayment}>
              {(() => {
                const propertyIsHMO = (property.property_type || property.propertyType || 'hmo') === 'hmo';
                return propertyIsHMO ? (
                  <div className="form-group"><label>Room *</label>
                    <select value={paymentForm.roomId} onChange={e => {
                      const room = rooms.find(r => String(r.id)===e.target.value);
                      setPaymentForm(p => ({ ...p, roomId:e.target.value, tenantId:'', amount:room?.rentAmount?String(room.rentAmount):p.amount }));
                    }} required>
                      <option value="">Select room...</option>
                      {rooms.map(r => <option key={r.id} value={r.id}>{r.roomName}{r.tenantName?' – '+r.tenantName:''}{r.status!=='occupied'?' (vacant now)':''}</option>)}
                    </select>
                  </div>
                ) : null;
              })()}
              <div className="form-grid">
                <div className="form-group"><label>Month *</label><input type="month" value={paymentForm.month} onChange={e=>setPaymentForm(p=>({...p,month:e.target.value, tenantId:''}))} required /></div>
                <div className="form-group"><label>Amount ({curSymbol}) *</label><input type="number" step="0.01" min="0.01" value={paymentForm.amount} onChange={e=>setPaymentForm(p=>({...p,amount:e.target.value}))} onWheel={e=>e.target.blur()} required placeholder="0.00" /></div>
                <div className="form-group"><label>Date Received *</label><input type="date" value={paymentForm.paidDate} onChange={e=>setPaymentForm(p=>({...p,paidDate:e.target.value}))} required /></div>
              </div>
              {(() => {
                const propertyIsHMO = (property.property_type || property.propertyType || 'hmo') === 'hmo';
                // BUG FIX: previously gated entirely on paymentForm.roomId
                // having a value — impossible for a non-HMO property, which
                // has no room field at all now (see above). This is why the
                // whole Tenant section never appeared for BTL/Single Let,
                // not just showed an empty dropdown.
                if (propertyIsHMO && !paymentForm.roomId) return null;
                const roomOccupants = roomOccupantsForPayment;
                return (
                  <div className="form-group">
                    <label>Tenant {roomOccupants.length===0 && <span style={{color:'var(--text-secondary)',fontWeight:400,fontSize:'0.75rem'}}>— no tenant record found for this {propertyIsHMO ? 'room' : 'property'}/month; add one via Tenants if you want this payment correctly attributed</span>}</label>
                    <select value={paymentForm.tenantId} onChange={e=>setPaymentForm(p=>({...p,tenantId:e.target.value}))}>
                      <option value="">— No specific tenant —</option>
                      {roomOccupants.map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name||''}{t.status==='moved_out'?' (moved out)':''}</option>)}
                    </select>
                  </div>
                );
              })()}
              {/* Quick expenses toggle — only relevant when recording a fresh payment, not editing an existing one */}
              {!editingPayment && (
              <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:'0.5rem', padding:'0.75rem', marginBottom:'1rem' }}>
                <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer', fontWeight:600, fontSize:'0.875rem', color:'#15803d' }}>
                  <input type="checkbox" checked={addExpenseAfterPayment} onChange={e=>setAddExpenseAfterPayment(e.target.checked)} style={{ width:'16px', height:'16px' }}/>
                  Also add expenses for this payment
                </label>
                {addExpenseAfterPayment && (
                  <div style={{ marginTop:'0.75rem' }}>
                    {quickExpenses.map((qe, i) => (
                      <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 100px 1fr auto', gap:'0.4rem', alignItems:'end', marginBottom:'0.5rem', paddingBottom:'0.5rem', borderBottom: i < quickExpenses.length-1 ? '1px solid #bbf7d0' : 'none' }}>
                        <div className="form-group" style={{ marginBottom:0 }}>
                          {i===0 && <label style={{ fontSize:'0.75rem' }}>Category</label>}
                          <select value={qe.category} onChange={e=>setQuickExpenseField(i,'category',e.target.value)}>
                            <option value="">Select...</option>
                            {EXPENSE_CATEGORIES.map(c=><option key={c}>{c}</option>)}
                          </select>
                        </div>
                        <div className="form-group" style={{ marginBottom:0 }}>
                          {i===0 && <label style={{ fontSize:'0.75rem' }}>Amount</label>}
                          <input type="number" step="0.01" value={qe.amount} onChange={e=>setQuickExpenseField(i,'amount',e.target.value)} onWheel={e=>e.target.blur()} placeholder="0.00"/>
                        </div>
                        <div className="form-group" style={{ marginBottom:0 }}>
                          {i===0 && <label style={{ fontSize:'0.75rem' }}>Description</label>}
                          <input value={qe.description} onChange={e=>setQuickExpenseField(i,'description',e.target.value)} placeholder="Optional notes"/>
                        </div>
                        <div style={{ display:'flex', gap:'0.3rem', alignItems:'center' }}>
                          <select value={qe.paid} onChange={e=>setQuickExpenseField(i,'paid',e.target.value==='true')} style={{ fontSize:'0.75rem', padding:'0.3rem' }}>
                            <option value="true">Paid</option>
                            <option value="false">Unpaid</option>
                          </select>
                          {quickExpenses.length > 1 && (
                            <button type="button" onClick={()=>removeQuickExpenseRow(i)} style={{ background:'none', border:'none', color:'#dc2626', cursor:'pointer', fontSize:'1rem', padding:'0 0.2rem' }}>×</button>
                          )}
                        </div>
                      </div>
                    ))}
                    <button type="button" onClick={addQuickExpenseRow} style={{ background:'none', border:'1px dashed #16a34a', color:'#16a34a', borderRadius:'0.375rem', padding:'0.3rem 0.75rem', cursor:'pointer', fontSize:'0.8rem', width:'100%', marginTop:'0.25rem' }}>
                      + Add another expense
                    </button>
                  </div>
                )}
              </div>
              )}
              <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowPaymentModal(false); setEditingPayment(null); setAddExpenseAfterPayment(false); setQuickExpenses([{ category:'', amount:'', description:'', paid:true }]); }}>Cancel</button>
                <button type="submit" className="btn btn-success" disabled={loading}>{loading?'Saving...':(editingPayment?'💾 Save Changes':'✅ Record Payment')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Expense Modal */}
      {showExpenseModal && (
        <div className="modal-overlay" onClick={() => setShowExpenseModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">📤 Add Property Expense</span><button className="modal-close" onClick={() => setShowExpenseModal(false)}>×</button></div>
            {error && <div className="alert alert-danger">{error}</div>}
            <form onSubmit={handleAddExpense}>
              <div className="form-grid">
                <div className="form-group"><label>Category *</label>
                  <select value={expenseForm.category} onChange={e=>setExpenseForm(p=>({...p,category:e.target.value}))} required>
                    <option value="">Select category...</option>
                    {categoryOptions(expenseForm.category).map(c=><option key={c}>{c}</option>)}
                  </select>
                  {categoryHint(expenseForm.category) && <small style={{display:'block',marginTop:'0.25rem',color:'var(--text-secondary)'}}>{categoryHint(expenseForm.category)}</small>}
                </div>
                <div className="form-group"><label>Amount ({curSymbol}) *</label><input type="number" step="0.01" min="0.01" value={expenseForm.amount} onChange={e=>setExpenseForm(p=>({...p,amount:e.target.value}))} onWheel={e=>e.target.blur()} required placeholder="0.00" /></div>
                <div className="form-group"><label>Date *</label><input type="date" value={expenseForm.date} onChange={e=>setExpenseForm(p=>({...p,date:e.target.value}))} required /></div>
                <div className="form-group"><label>Paid?</label>
                  <select value={expenseForm.paid} onChange={e=>setExpenseForm(p=>({...p,paid:e.target.value==='true'}))}>
                    <option value="false">No – Outstanding</option><option value="true">Yes – Paid</option>
                  </select>
                </div>
              </div>
              <div className="form-group"><label>Description</label><textarea value={expenseForm.description} onChange={e=>setExpenseForm(p=>({...p,description:e.target.value}))} rows="2" placeholder="Optional notes" /></div>
              <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowExpenseModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading?'Saving...':'✅ Add Expense'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Document Modal */}
      {showLicenceModal && (
        <div className="modal-overlay" onClick={() => { setShowLicenceModal(false); setPendingFile(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editLicence ? '✏️ Edit Document' : '📁 Add Document'}</span>
              <button className="modal-close" onClick={() => { setShowLicenceModal(false); setPendingFile(null); }}>×</button>
            </div>
            {error && <div className="alert alert-danger">{error}</div>}
            <form onSubmit={handleAddLicence}>
              <div className="form-group"><label>Type *</label>
                <select value={licenceForm.type} onChange={e=>setLicenceForm(p=>({...p,type:e.target.value}))} required>
                  <option value="">Select type...</option>
                  <optgroup label="Compliance (tracks expiry)">
                    {COMPLIANCE_TYPES.slice().sort().map(t=><option key={t}>{t}</option>)}
                  </optgroup>
                  <optgroup label="General (no expiry)">
                    {GENERAL_TYPES.map(t=><option key={t}>{t}</option>)}
                  </optgroup>
                </select>
              </div>
              <div className="form-group"><label>Description</label><input value={licenceForm.description} onChange={e=>setLicenceForm(p=>({...p,description:e.target.value}))} placeholder="e.g. Annual gas safety check" /></div>
              <div className="form-group"><label>Reference Number</label><input value={licenceForm.referenceNumber} onChange={e=>setLicenceForm(p=>({...p,referenceNumber:e.target.value}))} placeholder="e.g. HMO-2024-001" /></div>

              {/* Expiry fields — only for compliance types */}
              {isComplianceType(licenceForm.type) && (
                <div className="form-grid">
                  <div className="form-group"><label>Valid From</label><input type="date" value={licenceForm.validFrom} onChange={e=>setLicenceForm(p=>({...p,validFrom:e.target.value}))} /></div>
                  <div className="form-group">
                    <label>Valid To / Expiry Date</label>
                    <input type="date" value={licenceForm.validTo} onChange={e=>setLicenceForm(p=>({...p,validTo:e.target.value}))} />
                    <small style={{ color:'var(--text-secondary)', fontSize:'0.75rem' }}>A calendar reminder appears 30 days before expiry</small>
                  </div>
                </div>
              )}

              <div className="form-group"><label>Notes</label><textarea value={licenceForm.notes} onChange={e=>setLicenceForm(p=>({...p,notes:e.target.value}))} rows="2" placeholder="Additional notes..." /></div>

              {/* File upload at creation (optional) — new records only */}
              {!editLicence && (
                <div className="form-group">
                  <label>Attach File (optional)</label>
                  <input type="file" accept={ACCEPTED_FILES} onChange={e=>{setPendingFile(e.target.files[0]||null); setScanDocError(''); setScanDocSuccess(false);}} />
                  <small style={{ color:'var(--text-secondary)', fontSize:'0.75rem', display:'block', marginTop:'0.25rem' }}>{UPLOAD_HELP}</small>
                  {pendingFile && <small style={{ color:'var(--success)', fontSize:'0.75rem' }}>✓ {pendingFile.name} ready to upload</small>}
                  {pendingFile && (pendingFile.type==='application/pdf' || pendingFile.type==='image/jpeg' || pendingFile.type==='image/png') && (
                    <div style={{ marginTop:'0.5rem' }}>
                      <button type="button" className="btn btn-secondary" style={{ fontSize:'0.8rem' }} onClick={handleScanDocument} disabled={scanningDoc}>
                        {scanningDoc ? '🔍 Reading document…' : '📷 Scan to Auto-Fill'}
                      </button>
                      {scanDocSuccess && <span style={{ marginLeft:'0.6rem', fontSize:'0.75rem', color:'var(--success)' }}>✓ Filled in above — please check before saving</span>}
                      {scanDocError && <div style={{ marginTop:'0.4rem', fontSize:'0.75rem', color:'var(--danger)' }}>{scanDocError}</div>}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowLicenceModal(false); setPendingFile(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading?'Saving...':editLicence?'💾 Save Changes':'✅ Add Document'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Version Modal — date picker replaces window.prompt */}
      {versionModal && (
        <div className="modal-overlay" onClick={() => setVersionModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth:'420px' }}>
            <div className="modal-header">
              <span className="modal-title">⬆️ New Version — {versionModal.lic.type}</span>
              <button className="modal-close" onClick={() => setVersionModal(null)}>×</button>
            </div>
            <div style={{ padding:'0.25rem 0 0.5rem' }}>
              <p style={{ fontSize:'0.85rem', color:'var(--text-secondary)', marginBottom:'0.75rem' }}>
                Uploading <strong>{versionModal.file?.name}</strong> as a new version. If this renewal has a new expiry date, set it below — the document's calendar reminder will be updated.
              </p>
              <div className="form-group">
                <label>New Expiry Date</label>
                <input type="date" value={versionModal.expiry} onChange={e => setVersionModal(m => ({ ...m, expiry: e.target.value }))} />
                <small style={{ color:'var(--text-secondary)', fontSize:'0.75rem' }}>Leave as-is to keep the current expiry date.</small>
              </div>
              <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end', marginTop:'1rem' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setVersionModal(null)}>Cancel</button>
                <button type="button" className="btn btn-primary" disabled={uploadingFor === versionModal.lic.id}
                  onClick={async () => {
                    const { lic, file, expiry } = versionModal;
                    setVersionModal(null);
                    await doUpload(lic, file, expiry, true);
                  }}>
                  {uploadingFor === versionModal.lic.id ? 'Uploading…' : '⬆️ Upload New Version'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── BATCH SCAN DOCUMENTS MODAL ── */}
      {showBatchScan && (
        <div className="modal-overlay" onClick={()=>setShowBatchScan(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{ maxWidth: batchStep==='reviewing' ? '1000px' : '520px' }}>
            <div className="modal-header">
              <span className="modal-title">📷 Scan Document(s)</span>
              <button className="modal-close" onClick={()=>setShowBatchScan(false)}>×</button>
            </div>

            {batchStep === 'upload' && (
              <div>
                <p style={{ color:'var(--text-secondary)', marginBottom:'1.25rem', lineHeight:1.5 }}>
                  Upload several certificates or licences at once for this property — photos or PDFs. Each one is read and you'll get a chance to check and correct the details before anything is saved.
                </p>
                {batchError && <div className="alert alert-danger" style={{ marginBottom:'1rem' }}>{batchError}</div>}
                <div
                  onDragOver={e => { e.preventDefault(); setBatchDragActive(true); }}
                  onDragLeave={e => { e.preventDefault(); setBatchDragActive(false); }}
                  onDrop={e => {
                    e.preventDefault();
                    setBatchDragActive(false);
                    const dropped = Array.from(e.dataTransfer.files || []);
                    const valid = dropped.filter(f => ['image/jpeg','image/png','application/pdf'].includes(f.type));
                    if (valid.length) { setBatchFiles(valid); setBatchError(''); }
                    else if (dropped.length) { setBatchError('Please drop JPEG, PNG or PDF files only.'); }
                  }}
                  onClick={() => document.getElementById('batch-scan-input').click()}
                  className="card"
                  style={{
                    border: `2px dashed ${batchDragActive ? 'var(--brand-green)' : 'var(--border)'}`,
                    textAlign: 'center',
                    cursor: 'pointer',
                    padding: '2.5rem 1.5rem',
                    background: batchDragActive || batchFiles.length ? 'var(--bg-light)' : 'transparent',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <input
                    id="batch-scan-input"
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,application/pdf"
                    onChange={e => { setBatchFiles(Array.from(e.target.files || [])); setBatchError(''); }}
                    style={{ display: 'none' }}
                  />
                  {batchFiles.length > 0 ? (
                    <div>
                      <span style={{ fontSize: '2.5rem' }}>✅</span>
                      <h3 style={{ margin:'0.5rem 0 0.25rem', color:'var(--brand-green)' }}>{batchFiles.length} file{batchFiles.length!==1?'s':''} ready to scan</h3>
                      <p style={{ color:'var(--text-secondary)', fontSize:'0.85rem', margin:0 }}>{batchFiles.map(f=>f.name).join(', ')}</p>
                      <p style={{ color:'var(--text-secondary)', fontSize:'0.8rem', marginTop:'0.5rem' }}>Click or drop again to choose different files</p>
                    </div>
                  ) : (
                    <div>
                      <span style={{ fontSize: '2.5rem' }}>📄</span>
                      <h3 style={{ margin:'0.5rem 0 0.25rem' }}>Drag and drop documents here</h3>
                      <p style={{ color:'var(--text-secondary)', fontSize:'0.85rem', margin:0 }}>or click to browse — select multiple at once — JPEG, PNG or PDF</p>
                    </div>
                  )}
                </div>
                <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end', marginTop:'1.5rem' }}>
                  <button type="button" className="btn btn-secondary" onClick={()=>setShowBatchScan(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={handleBatchScanSubmit} disabled={!batchFiles.length}>
                    🔍 Scan {batchFiles.length > 1 ? `${batchFiles.length} Documents` : 'Document'}
                  </button>
                </div>
              </div>
            )}

            {batchStep === 'scanning' && (
              <div style={{ textAlign:'center', padding:'3rem 1rem' }}>
                <div className="loading-spinner" style={{ margin:'0 auto 1.5rem' }}></div>
                <h3 style={{ margin:'0 0 0.5rem' }}>Reading document {batchProgress.current} of {batchProgress.total}…</h3>
                <p style={{ color:'var(--text-secondary)', fontSize:'0.85rem' }}>{batchFiles[batchProgress.current-1]?.name}</p>
              </div>
            )}

            {batchStep === 'reviewing' && (
              <div>
                {batchFileErrors.length > 0 && (
                  <div className="alert alert-danger" style={{ marginBottom:'1rem' }}>
                    ⚠️ Couldn't read: {batchFileErrors.map(f => `"${f.file}" (${f.error})`).join('; ')} — everything else scanned fine.
                  </div>
                )}
                {batchError && <div className="alert alert-danger" style={{ marginBottom:'1rem' }}>{batchError}</div>}
                <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:'0.5rem', padding:'0.75rem 1rem', marginBottom:'1.25rem', fontSize:'0.85rem', color:'#1e40af' }}>
                  Found {batchReviewItems.length} document{batchReviewItems.length!==1?'s':''}. Check each one against its preview — nothing is saved until you click "Save All Selected."
                </div>

                <div style={{ maxHeight:'480px', overflowY:'auto' }}>
                  {batchReviewItems.map((item, idx) => {
                    const preview = batchPreviews[item.fileIndex];
                    return (
                      <div key={idx} className="card" style={{ marginBottom:'1.25rem', opacity: item.include ? 1 : 0.5 }}>
                        <div style={{ display:'flex', gap:'1.25rem', alignItems:'flex-start', flexWrap:'wrap' }}>
                          <div style={{ flexShrink:0 }}>
                            {preview?.isPdf ? (
                              <a href={preview.url} target="_blank" rel="noreferrer" style={{ display:'block', width:'150px', border:'1px solid var(--border)', borderRadius:'0.5rem', padding:'1rem', textAlign:'center', textDecoration:'none', color:'var(--text-secondary)' }}>
                                <div style={{ fontSize:'2rem' }}>📄</div>
                                <div style={{ fontSize:'0.75rem', fontWeight:600, marginTop:'0.4rem' }}>View Original PDF</div>
                              </a>
                            ) : preview ? (
                              <a href={preview.url} target="_blank" rel="noreferrer">
                                <img src={preview.url} alt={preview.name} style={{ width:'150px', height:'auto', maxHeight:'200px', objectFit:'contain', border:'1px solid var(--border)', borderRadius:'0.5rem', display:'block' }} />
                                <div style={{ fontSize:'0.7rem', color:'var(--text-secondary)', textAlign:'center', marginTop:'0.3rem' }}>Click to enlarge</div>
                              </a>
                            ) : (
                              <div style={{ width:'150px', height:'110px', border:'1px dashed var(--border)', borderRadius:'0.5rem', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-secondary)', fontSize:'0.75rem' }}>No preview</div>
                            )}
                          </div>

                          <div style={{ flex:1, minWidth:'320px' }}>
                            <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', fontWeight:600, marginBottom:'0.75rem', cursor:'pointer' }}>
                              <input type="checkbox" checked={item.include} onChange={e=>updateBatchItem(idx,{include:e.target.checked})} />
                              {preview?.name || `Document ${idx+1}`}
                            </label>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem', marginBottom:'0.6rem' }}>
                              <div>
                                <label style={{ fontSize:'0.7rem' }}>Type</label>
                                <select value={item.type} onChange={e=>updateBatchItem(idx,{type:e.target.value})}>
                                  {ALL_DOC_TYPES.map(t => <option key={t}>{t}</option>)}
                                </select>
                              </div>
                              <div>
                                <label style={{ fontSize:'0.7rem' }}>Reference Number</label>
                                <input type="text" value={item.referenceNumber} onChange={e=>updateBatchItem(idx,{referenceNumber:e.target.value})} />
                              </div>
                            </div>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem', marginBottom:'0.6rem' }}>
                              <div>
                                <label style={{ fontSize:'0.7rem' }}>Issue Date</label>
                                <input type="date" value={item.validFrom} onChange={e=>updateBatchItem(idx,{validFrom:e.target.value})} />
                              </div>
                              <div>
                                <label style={{ fontSize:'0.7rem' }}>Expiry Date</label>
                                <input type="date" value={item.validTo} onChange={e=>updateBatchItem(idx,{validTo:e.target.value})} />
                              </div>
                            </div>
                            <div>
                              <label style={{ fontSize:'0.7rem' }}>Description</label>
                              <input type="text" value={item.description} onChange={e=>updateBatchItem(idx,{description:e.target.value})} />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end', marginTop:'1.25rem' }}>
                  <button type="button" className="btn btn-secondary" onClick={()=>setShowBatchScan(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={handleBatchSave} disabled={batchSaving}>
                    {batchSaving ? 'Saving…' : `✅ Save All Selected (${batchReviewItems.filter(r=>r.include).length})`}
                  </button>
                </div>
              </div>
            )}

            {batchStep === 'done' && (
              <div>
                <div className="alert alert-success" style={{ marginBottom:'1rem' }}>
                  ✅ Done — {batchAddedLog.filter(l=>l.startsWith('✅')).length} of {batchAddedLog.length} document{batchAddedLog.length!==1?'s':''} saved.
                </div>
                <ul style={{ margin:0, paddingLeft:'1.2rem', fontSize:'0.85rem' }}>
                  {batchAddedLog.map((label, i) => <li key={i} style={{ marginBottom:'0.3rem' }}>{label}</li>)}
                </ul>
                <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'1.25rem' }}>
                  <button type="button" className="btn btn-primary" onClick={()=>setShowBatchScan(false)}>Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Create Photo Set Modal */}
      {showSetModal && (
        <div className="modal-overlay" onClick={() => setShowSetModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth:'440px' }}>
            <div className="modal-header">
              <span className="modal-title">🖼️ New Photo Set</span>
              <button className="modal-close" onClick={() => setShowSetModal(false)}>×</button>
            </div>
            {error && <div className="alert alert-danger">{error}</div>}
            <div style={{ padding:'0.25rem 0 0.5rem' }}>
              <div className="form-group">
                <label>Set Name *</label>
                <input value={newSetName} onChange={e => setNewSetName(e.target.value)} placeholder="e.g. Room 1, Marketing Photos, Exterior" autoFocus />
                <small style={{ color:'var(--text-secondary)', fontSize:'0.75rem' }}>Name this set so you can group photos (e.g. per room for listings).</small>
              </div>
              <div className="form-group">
                <label>Select Photos</label>
                <input type="file" accept=".jpg,.jpeg,.png" multiple id="newSetFiles" />
                <small style={{ color:'var(--text-secondary)', fontSize:'0.75rem', display:'block', marginTop:'0.25rem' }}>JPEG or PNG · up to 10MB each · select multiple at once</small>
              </div>
              <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end', marginTop:'1rem' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowSetModal(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" disabled={!!uploadingSet}
                  onClick={() => {
                    const input = document.getElementById('newSetFiles');
                    const files = input && input.files ? Array.from(input.files) : [];
                    if (!files.length) { setError('Select at least one photo'); return; }
                    createPhotoSet(newSetName, files);
                  }}>
                  {uploadingSet ? 'Uploading…' : 'Create Set'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox for viewing a photo full-size */}
      {lightbox && (
        <div className="modal-overlay" onClick={() => setLightbox(null)} style={{ zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth:'90vw', maxHeight:'90vh', display:'flex', flexDirection:'column', alignItems:'center' }}>
            <img src={lightbox.url} alt={lightbox.caption||''} style={{ maxWidth:'90vw', maxHeight:'82vh', objectFit:'contain', borderRadius:'0.5rem' }} />
            {lightbox.caption && <div style={{ color:'#fff', marginTop:'0.5rem', fontSize:'0.9rem' }}>{lightbox.caption}</div>}
            <button className="btn btn-secondary" style={{ marginTop:'0.75rem' }} onClick={() => setLightbox(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
